import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle,
  CalendarClock,
  Check,
  CheckCircle2,
  Loader2,
  Pencil,
  Play,
  Square,
  Undo2,
  Zap,
} from 'lucide-react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { DateTimePanel } from '@/components/ui/date-time-panel'
import { formatPlanned, fromLocalInput } from '@/lib/planningDates'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { cn } from '@/lib/utils'
import { FEATURES } from '@/lib/featureFlags'
import { useFeatureFlag } from '@/providers/FeatureFlagsProvider'
import { FeatureDisabledNote } from '@/components/auth/FeatureDisabledNote'
import type { FieldDefinition } from '@/types/cgt'
import type {
  AvailabilitySlot,
  EquipmentReservation,
  EquipmentSelection,
  InventoryProposal,
  InventorySelection,
  ProcessStepConnection,
  ProcessStepExecution,
  ProcessStepExecutionStatus,
  ReservationConflict,
  ReserveEquipmentItem,
  ReserveLine,
  ScanRefusal,
} from '@/api/processStepsApi'
import { isReservationConflict, isScanRefusal } from '@/api/processStepsApi'
import type { ApiEquipmentUnit } from '@/api/equipmentApi'
import { PROCESS_STEP_STATUS_META } from '@/lib/processStepStatus'
import { useProcessMaterialLots } from '@/components/process/materials'
import { readAttributeRow } from '@/lib/processAttributes'
import {
  useAssignExecution,
  useAvailabilityForWindows,
  useInventoryProposal,
  useProcessFieldDefs,
  useProcessStepConnection,
  useProcessStepExecutions,
  useProcessStepReservations,
  useProcessStepTransition,
  useReleaseReservation,
  useReserveEquipment,
  useReserveInventory,
  useReturnInventory,
  useStepHoldsFresh,
  useStepOpenHolds,
  useSetPlannedStart,
  useStartReservation,
  useRetimeReservations,
  useScanEquipment,
  useUnassignExecution,
  useUndoStart,
  useUpdateSelections,
} from '@/hooks/useProcessSteps'
import { useProcessAssignableUsers } from '@/hooks/useProcessAssignableUsers'
import { useEquipmentTypes, useEquipmentUnits } from '@/hooks/useEquipment'
import { useAuth } from '@/providers/AuthProvider'
import { usePermission, PERMISSIONS } from '@/lib/permissions'
import { mintSigningTokenFor } from '@/lib/signingToken'
import { useTenantSigners } from '@/hooks/useSigners'
import { SignerPicker } from '@/components/signature/SignerPicker'
import {
  restrictToAssignee,
  signerBlocker,
  signerChoices,
  signerName as chosenSignerName,
} from '@/lib/signerChoices'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { allocatedQty, batchHint, reservationSummary } from '@/lib/process/allocation'
import { reservationWindow } from '@/lib/process/reservationWindow'
import { retimePreview } from '@/lib/process/retimePreview'
import { partitionUnitOptions } from '@/lib/process/unitOptions'
import { MaterialLotAllocator } from '@/components/renderer/process/MaterialLotAllocator'
import { EquipmentScanField } from '@/components/renderer/process/EquipmentScanField'
import { RetimeConfirmDialog } from '@/components/renderer/process/RetimeConfirmDialog'
import { ProcessSignDialog } from '@/components/renderer/process/ProcessSignDialog'
import { AutoSelectConfirmDialog } from '@/components/renderer/process/AutoSelectConfirmDialog'
import { PrintPickListButton } from '@/components/pick-lists/PrintPickListButton'
import {
  TransitionConfirmScreen,
  TRANSITION_HEADING,
} from '@/components/renderer/process/TransitionConfirmScreen'

// ─── Status / action metadata ────────────────────────────────────────────────

// Shared with My Work, so a step's status reads the same in both places.
const STATUS_META = PROCESS_STEP_STATUS_META

type TransitionMode = 'start' | 'end' | 'complete'

// What each status offers as its next signed transition (COMPLETED offers none).
//
// `icon`/`tone` are carried explicitly rather than derived from `mode`, because
// the STARTED entry deliberately breaks the old correspondence: its mode is
// still `end`, but the button that opens it is no longer the stop itself.
const NEXT_ACTION: Partial<
  Record<
    ProcessStepExecutionStatus,
    {
      mode: TransitionMode
      label: string
      to: ProcessStepExecutionStatus
      icon: typeof Play
      tone: 'primary' | 'green'
    }
  >
> = {
  ASSIGNED: { mode: 'start', label: 'Initiate', to: 'STARTED', icon: Play, tone: 'primary' },
  // Reads "Continue", not "Stop": mid-run the operator's actual job is to keep
  // recording what the step used, and the modal this opens is that working
  // screen. Stopping is one deliberate click further in, on the modal's own
  // confirm button — not the default action on an always-visible card.
  STARTED: { mode: 'end', label: 'Continue', to: 'PENDING', icon: Play, tone: 'primary' },
  PENDING: {
    mode: 'complete',
    label: 'Complete',
    to: 'COMPLETED',
    icon: CheckCircle2,
    tone: 'green',
  },
}

// 21 CFR Part 11 signing meaning per transition.
/**
 * Sentinel for the assignee dropdown's "Unassigned" entry. Radix forbids an
 * empty SelectItem value (that means "no selection"), so clearing the owner
 * needs a value of its own that cannot collide with a user id.
 */
const UNASSIGN_VALUE = '__unassign__'

const SIGNING_MEANING: Record<TransitionMode, string> = {
  start: 'Performed by',
  end: 'Ended by',
  complete: 'Reviewed and completed by',
}

// On a kit run these two transitions ARE the kit's two signatures — "kitted by"
// and "verified by". They may be the same person: a second-person sign-off is a
// form's Review field, not a rule on a process step. The meaning is what the
// Part 11 audit row records, so it has to say what was actually attested.
const KIT_SIGNING_MEANING: Partial<Record<TransitionMode, string>> = {
  end: 'Kitted by',
  complete: 'Kit verified by',
}

function signingMeaning(mode: TransitionMode, isKitStep?: boolean): string {
  return (isKitStep && KIT_SIGNING_MEANING[mode]) || SIGNING_MEANING[mode]
}

// Undo is not a TransitionMode — it has no modal of its own, it is a panel
// inside the End modal — so its meaning lives beside the map rather than in it.
const UNDO_SIGNING_MEANING = 'Start voided by'

// `ASSIGNED` is the enum's initial state, not a claim that someone owns the
// step — so a step with no assignee must not read "Assigned". Showing it as
// Unassigned is what actually tells the operator why Initiate is disabled.
function StatusChip({
  status,
  assigned,
}: {
  status: ProcessStepExecutionStatus
  assigned?: boolean
}) {
  const unassigned = status === 'ASSIGNED' && assigned === false
  const meta = STATUS_META[status]
  return (
    <span
      // The word "Assigned" also appears as the assignment dropdown's column
      // label, so the chip needs an identity of its own to be addressable.
      data-testid='process-step-status'
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
        unassigned ? 'bg-amber-50 text-amber-700' : meta.chip,
      )}
    >
      <span
        className={cn('h-1.5 w-1.5 rounded-full', unassigned ? 'bg-amber-500' : meta.dot)}
      />
      {unassigned ? 'Unassigned' : meta.label}
    </span>
  )
}

/**
 * What the booking automation has achieved, next to the field that drives it.
 *
 * Four states, and the distinction that matters is between the last two: "every
 * unit is held" and "some are" look identical in a greyed-out input, but they
 * mean different things when the operator is about to sign a Start. Partial is
 * therefore stated with its numbers rather than rounded up to Reserved.
 *
 * Renders nothing when a step configures no equipment — there is no automation
 * to report on, and an empty chip is worse than no chip.
 */
function AutomationStatus({
  held,
  total,
  busy,
  busyLabel,
  className,
}: {
  held: number
  total: number
  busy: boolean
  busyLabel: string
  /** Lets a caller tighten the padding when this sits inline with a label. */
  className?: string
}) {
  if (total === 0) return null

  const [tone, dot, text] = busy
    ? (['bg-blue-50 text-blue-800', 'bg-blue-500 animate-pulse', busyLabel] as const)
    : held === 0
      ? (['bg-gray-100 text-gray-600', 'bg-gray-400', 'Not reserved'] as const)
      : held === total
        ? ([
            'bg-green-50 text-green-800',
            'bg-green-600',
            `Reserved · ${total} unit${total === 1 ? '' : 's'}`,
          ] as const)
        : ([
            'bg-amber-50 text-amber-800',
            'bg-amber-500',
            `Reserved · ${held} of ${total}`,
          ] as const)

  // Complete gets a TICK rather than another coloured dot. "Everything is held"
  // is the state an operator scans for before signing a Start, and it should be
  // recognisable without reading the count beside it.
  const done = !busy && held === total

  return (
    <span
      // Polite, not assertive: this updates as bookings land and should not
      // interrupt an operator mid-scan.
      aria-live='polite'
      className={cn(
        'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium',
        tone,
        className,
      )}
    >
      {done ? (
        <CheckCircle2 className='h-3 w-3 shrink-0' />
      ) : (
        <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', dot)} />
      )}
      {text}
    </span>
  )
}

/** Clock only, monospaced — these are read down a column and compared. */
function clockOf(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

/**
 * The full date a timestamp falls on — day, month AND year, always.
 *
 * Printed under every time in the strip, because these three genuinely can land
 * on different days: a run started late on Tuesday ends on Wednesday, and a
 * clock alone would say "08:48" for an end fourteen hours after the start it
 * sits beside.
 *
 * The year is never dropped, even for the current one. A manufacturing record
 * is read back long after the run — in an audit, an investigation, a transfer
 * of care — and "Sep 5" is only unambiguous to someone who already knows which
 * year they are looking at. The reader of a regulated record is exactly the
 * person who does not.
 */
function dayOf(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' })
}

/**
 * The three states a unit passes through, counted.
 *
 * Assigned → reserved → verified is the actual progression, and they are
 * different questions: a unit can be picked without being held, and held
 * without anyone having been to it. One number could not answer all three.
 */
function UnitCounters({
  assigned,
  reserved,
  verified,
  total,
}: {
  assigned: number
  reserved: number
  verified: number
  total: number
}) {
  if (total === 0) return null
  const cells = [
    { label: 'Assigned', value: assigned },
    { label: 'Reserved', value: reserved },
    // Same word the rows use. A counter that says "Verified" over rows that say
    // "In use" reads as two different tallies.
    { label: 'In use', value: verified },
  ]
  return (
    <div className='flex shrink-0 items-start gap-5'>
      {cells.map(c => (
        <div key={c.label} className='text-right'>
          <p
            className={cn(
              'font-mono text-sm tabular-nums',
              // Complete reads differently from in-progress at a glance, which
              // is what an operator checks before signing a Start.
              c.value === total ? 'font-semibold text-green-700' : 'text-gray-900',
            )}
          >
            {c.value}/{total}
          </p>
          <p className='text-[10px] uppercase tracking-wide text-gray-500'>{c.label}</p>
        </div>
      ))}
    </div>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className='text-[11px] font-semibold uppercase tracking-wide text-gray-400'>{children}</p>
  )
}

// `<input type='datetime-local'>` speaks local "YYYY-MM-DDTHH:mm"; the API speaks
// ISO. Blank / half-typed values yield undefined so they're simply omitted.
function localToIso(local: string): string | undefined {
  if (!local) return undefined
  const d = new Date(local)
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString()
}

// The inverse of localToIso: an ISO instant back into the local wall-clock
// string `<input type='datetime-local'>` expects.
function isoToLocalInput(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function formatStamp(iso: string): string {
  return new Date(iso).toLocaleString()
}

// ─── Start-mode lot allocator (one material, expandable) ─────────────────────
// Collapsed: material + required qty + allocation indicator (the auto-suggested
// pick is already applied). Checking the row expands its lot list to adjust or
// split the allocation across lots.

// `isExpiredLot`, `tidyQty` and `MaterialLotAllocator` moved to
// components/renderer/process/ and lib/process/allocation.ts — this file
// imports them rather than keeping a second copy.

// ─── Configured-process tabs (shared by the row's modal) ─────────────────────
// The modal shows the process AS CONFIGURED on the builder's Process tab —
// grouped into Equipment / Inventory / Staffing / Attributes tabs, never
// maintenance fields. In start mode the Equipment tab hosts a unit picker per
// configured type; in end/complete modes it shows the units assigned at start.

function ConfiguredProcessTabs({
  submissionId,
  fieldId,
  mode,
  connection,
  selections,
  onSelectEquipment,
  assigned,
  lotAllocations,
  onAllocateLot,
  onAllocateLotMany,
  reservedInventory,
  onReserveInventory,
  assignedInventory,
  shortProposals,
  expectedStartIso,
  startOverrides,
  onOverrideStart,
  onRetimeRow,
  retimingRow,
  rowRetimeError,
  onStartUnit,
  startingUnit,
  reservations,
  onReserve,
  onRelease,
  onScan,
  scanPendingFor,
  scanRefusal,
  lastScanVerified,
  availabilityFor,
  fallbackStart,
}: {
  /** Passed through to the allocator so a material's open hold can be returned. */
  submissionId: string
  fieldId: string
  /** Drives the allocator's wording — stock is reserved at start, consumed at end. */
  mode: TransitionMode
  connection: ProcessStepConnection
  /** Start mode: chosen unit per requirement id — renders the unit pickers. */
  selections?: Record<string, string>
  onSelectEquipment?: (requirementId: string, equipmentId: string) => void
  /** End/complete modes: units assigned when the step was started. */
  assigned?: EquipmentSelection[] | null
  /**
   * The step's ONE expected start, as an ISO instant — entered once in the
   * modal header, not per equipment row.
   *
   * Each requirement's own window is DERIVED from it (plus that requirement's
   * own `prepHrs`), never typed: the operator plans a step, not eight
   * independent machine bookings, and eight editable times were eight chances
   * for them to disagree with each other and with the run.
   *
   * A row can now be detached from it by hand — see `startOverrides` — but that
   * is an explicit act with a Reset beside it, not the default.
   */
  expectedStartIso?: string | null
  /**
   * Hand-picked window starts per requirement id, as `datetime-local` strings.
   * An entry means "want this unit at exactly this time"; prep does not apply on
   * top, because the operator has stated what prep exists to compute.
   */
  startOverrides?: Record<string, string>
  /** Set a row's picked start; an empty value resets it to the derived time. */
  onOverrideStart?: (requirementId: string, value: string) => void
  /** Move an already-held row's booking to a picked time. */
  onRetimeRow?: (requirementId: string, value: string) => void
  /** The row whose hold is mid-move, so only its picker shows pending. */
  retimingRow?: string | null
  rowRetimeError?: { id: string; message: string } | null
  /**
   * Stamp a held unit's actual start without a scan. Recorded by the server as
   * `started_manually`, so the trail keeps it distinct from a verified scan.
   */
  onStartUnit?: (reservationId: string) => void
  /** The reservation mid-start, so only its own control shows pending. */
  startingUnit?: string | null
  /** Start mode: qty allocated per requirement id → lot — renders the lot inputs. */
  lotAllocations?: Record<string, Record<string, string>>
  onAllocateLot?: (
    requirementId: string,
    lot: string,
    qty: string,
    /** A finished value — the only kind that may release a hold. */
    commit?: boolean,
  ) => void
  /** Several lots at once, already settled server-side; releases nothing. */
  onAllocateLotMany?: (requirementId: string, updates: Record<string, string>) => void
  /**
   * Start mode: how much of each requirement's lot picks is ALREADY reserved
   * server-side via the "Reserve" button (z8nrz7dyfp follow-up) — the delta
   * against `lotAllocations` is what the button still has to submit.
   */
  reservedInventory?: Record<string, Record<string, number>>
  /** Reserve every currently-ticked, not-yet-reserved line across every requirement at once — mirrors `onReserve` for equipment. */
  onReserveInventory?: (items: { requirementId: string; lines: ReserveLine[] }[]) => void
  /** End/complete modes: lots allocated when the step was started. */
  assignedInventory?: InventorySelection[] | null
  /** Requirements the FEFO proposal could not cover, by requirement id. */
  shortProposals?: Map<string, InventoryProposal>
  /** Start mode: live holds for this step's equipment requirements. */
  reservations?: EquipmentReservation[]
  onReserve?: (items: ReserveEquipmentItem[]) => void
  onRelease?: (reservationId: string) => void
  /**
   * Submit a tag scanned INTO a specific requirement row. Resolves true when
   * the server verified the unit and held it. Absent when not editable.
   *
   * The requirement is passed because each row owns a scan field: the operator
   * is asserting "this machine satisfies THIS booking", and the server treats
   * that as binding rather than resolving the row itself.
   */
  onScan?: (requirementId: string, scannedCode: string) => Promise<boolean>
  /**
   * The requirement whose scan is in flight, or null.
   *
   * Scoped to one row rather than a plain boolean: every row owns a scan field,
   * and a global flag would put all of them into "Checking…" and read-only
   * because one was scanned.
   */
  scanPendingFor?: string | null
  /** The server's refusal, rendered only on the row it names. */
  scanRefusal?: ScanRefusal | null
  /** The unit the last verified scan held, and which row it filled. */
  lastScanVerified?: { requirementId: string; label: string } | null
  /**
   * Advisory availability for a unit, in the window the operator has chosen.
   * Undefined when no window is known yet — a unit's freeness is meaningless
   * without one, so nothing is claimed rather than claiming "free".
   */
  availabilityFor?: (requirementId: string, equipmentId: string) => AvailabilitySlot | undefined
  /**
   * The run's actual start, shown read-only where a requirement has no expected
   * start of its own — the operator often sets the time once in the header
   * rather than per requirement.
   */
  fallbackStart?: string | null
}) {
  const equipmentOn = useFeatureFlag(FEATURES.equipment)
  const inventoryOn = useFeatureFlag(FEATURES.inventory)
  const { data: equipmentTypes } = useEquipmentTypes()
  const { data: equipmentUnits } = useEquipmentUnits()
  const typeName = useMemo(() => {
    const map = new Map<string, string>()
    for (const t of equipmentTypes ?? []) map.set(t.id, t.name)
    return (id: string) => map.get(id) ?? id
  }, [equipmentTypes])
  const unitsByType = useMemo(() => {
    const map = new Map<string, ApiEquipmentUnit[]>()
    for (const u of equipmentUnits ?? []) {
      const list = map.get(u.equipmentTypeId) ?? []
      list.push(u)
      map.set(u.equipmentTypeId, list)
    }
    return map
  }, [equipmentUnits])
  const assignedByRequirement = useMemo(
    () => new Map((assigned ?? []).map(s => [s.requirementId, s])),
    [assigned],
  )
  const assignedInventoryByRequirement = useMemo(
    () => new Map((assignedInventory ?? []).map(s => [s.requirementId, s])),
    [assignedInventory],
  )
  const reservationByRequirement = useMemo(
    () => new Map((reservations ?? []).map(res => [res.requirementId, res])),
    [reservations],
  )
  // Shared query key with the authoring panel, so the two cannot describe the
  // same attribute differently.
  const { data: fieldDefsData } = useProcessFieldDefs()
  const fieldDefs = useMemo(() => fieldDefsData ?? [], [fieldDefsData])

  /**
   * The step's attributes, as `label: value` chips.
   *
   * Read THROUGH THE DEFINITIONS, not straight off the JSONB, for three
   * reasons the raw map cannot answer: the definition holds the human LABEL (the
   * map is keyed `numberOfResources`), it holds the authored ORDER, and it holds
   * the data type that decides how a value reads.
   *
   * ACTIVE IS WHAT SHOWS — the value is not a second condition. This used to
   * skip any entry whose value was null or empty, so an attribute the author had
   * switched on but not typed into was invisible here: a step with Full Day and
   * Priority both switched on read "No attributes configured." to the person
   * performing it. A YES/NO attribute is carried by its flag alone and has no
   * value to type, so that filter hid the whole class of them.
   *
   * The METHOD comes from `connection.methods`, since a method's value is
   * membership in `MethodProcessStep` rather than a pair in that map. Without it
   * a step plainly configured with one read as unconfigured while the authoring
   * table beside it counted one.
   *
   * A stored key no definition covers is skipped, which is the same answer the
   * authoring panel and the server's `validate` already give it: an attribute
   * whose definition was deleted is not one the step still records.
   */
  const attributes = useMemo<[string, string][]>(() => {
    const rows: [string, string][] = []

    const map =
      connection.attributes && typeof connection.attributes === 'object'
        ? (connection.attributes as Record<string, unknown>)
        : {}

    for (const def of fieldDefs) {
      if (def.relational) {
        const methods = connection.methods ?? []
        if (methods.length > 0) rows.push([def.label, methods.map(m => m.name).join(', ')])
        continue
      }

      // Each kind decides for itself whether the step records it, in one place
      // — a flag prints its answer either way it points, a value prints only
      // when one was entered.
      const text = readAttributeRow(def, map[def.key])
      if (text !== null) rows.push([def.label, text])
    }
    return rows
  }, [connection.attributes, connection.methods, fieldDefs])

  const counts = {
    equipment: connection.equipmentRequirements.length,
    inventory: connection.inventoryRequirements.length,
    staffing: connection.staffingLines.length,
    attributes: attributes.length,
  }

  // ── Which materials are expanded ──────────────────────────────────────────
  //
  // Only the FIRST opens by default. A step can configure a dozen materials,
  // and expanding every one of them buried the tabs and the step's own actions
  // under a page of lot lists. The others are one click away, and their header
  // still carries the "0 of 5 reserved" chip, so nothing about the outstanding
  // work is hidden by being collapsed.
  //
  // `null` means "untouched" rather than "none open", so the default survives a
  // connection that loads after first render — a Set seeded in useState would
  // have been built from an empty requirement list and never corrected.
  const inventoryIds = connection.inventoryRequirements.map(r => r.id)
  const [openInventory, setOpenInventory] = useState<Set<string> | null>(null)
  const currentlyOpen = openInventory ?? new Set(inventoryIds.slice(0, 1))
  const isInventoryOpen = (id: string) => currentlyOpen.has(id)
  const allInventoryExpanded =
    inventoryIds.length > 0 && inventoryIds.every(id => currentlyOpen.has(id))

  const toggleInventory = (id: string) => {
    const next = new Set(currentlyOpen)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setOpenInventory(next)
  }
  const setAllInventoryOpen = (openAll: boolean) =>
    setOpenInventory(openAll ? new Set(inventoryIds) : new Set())

  // How many rows in each group the operator has actually dealt with. Only
  // Equipment and Inventory have a notion of "filled in" — a staffing line or an
  // attribute is configuration, not something to complete here — so only those
  // two report a fraction. Inventing a denominator for the others would imply
  // work that does not exist.
  const equipmentFilled = connection.equipmentRequirements.filter(
    r => !!selections?.[r.id] || !!reservationByRequirement.get(r.id),
  ).length
  const inventoryFilled = connection.inventoryRequirements.filter(r =>
    Object.values(lotAllocations?.[r.id] ?? {}).some(v => Number(v) > 0),
  ).length

  const TAB_DEFS = [
    {
      value: 'equipment',
      label: 'Equipment',
      count: counts.equipment,
      filled: counts.equipment > 0 ? equipmentFilled : null,
    },
    {
      value: 'inventory',
      label: 'Inventory',
      count: counts.inventory,
      filled: counts.inventory > 0 ? inventoryFilled : null,
    },
    { value: 'staffing', label: 'Staffing', count: counts.staffing, filled: null },
    { value: 'attributes', label: 'Attributes', count: counts.attributes, filled: null },
  ]
  // Land on the first group that actually has configuration.
  const defaultTab = TAB_DEFS.find(t => t.count > 0)?.value ?? 'equipment'

  // "Reserve" books every requirement that has a unit, a time, and no live hold
  // already — a real conflict-safe hold, not a local auto-fill. Excluding the
  // already-held ones is what lets the button go quiet once everything is
  // booked, and stops a re-click churning the audit trail with
  // release/re-reserve pairs for holds that were already correct.
  const equipmentReqs = connection.equipmentRequirements

  // The window this requirement will be held for, from the step's ONE expected
  // start plus the requirement's OWN prep. Shared with the availability probe in
  // the modal so the preview and the booking can never describe different
  // windows.
  const derivedWindow = (r: {
    id: string
    prepHrs?: number
    setupHrs: number
    runHrs: number
    unloadHrs: number
  }) => {
    // An override IS the window start, so prep is zeroed rather than added —
    // mirroring what the server does with `wantedAt`. Computing it differently
    // here would make the preview disagree with the booking it requests.
    const picked = localToIso(startOverrides?.[r.id] ?? '')
    if (picked) return reservationWindow(picked, { ...r, prepHrs: 0 })
    return expectedStartIso ? reservationWindow(expectedStartIso, r) : null
  }

  const alreadyHeld = equipmentReqs.filter(r => !!reservationByRequirement.get(r.id))
  const readyToReserve = equipmentReqs.filter(
    r => !reservationByRequirement.get(r.id) && !!selections?.[r.id] && !!derivedWindow(r),
  )
  // Distinguish "nothing left to book" from "you haven't filled anything in" —
  // a disabled button means opposite things in those two cases.
  const allReserved = equipmentReqs.length > 0 && alreadyHeld.length === equipmentReqs.length
  // Reserving needs BOTH a unit and a time, and naming only the missing half is
  // the difference between a control that looks broken and one that tells the
  // operator what to do next. This used to live in a `title`, which is a
  // hover-only affordance on the one control people complain does nothing.
  const missingUnit = equipmentReqs.filter(
    r => !reservationByRequirement.get(r.id) && !selections?.[r.id],
  ).length
  // One missing start now blocks every row, because there is only one to set —
  // so say that, rather than counting rows as though they were independent.
  const noRunTime = equipmentReqs.filter(
    r => !reservationByRequirement.get(r.id) && !!selections?.[r.id] && !derivedWindow(r),
  ).length
  const reserveHint = allReserved
    ? 'Every configured unit is reserved. Release one to change it.'
    : readyToReserve.length > 0
      ? `Holds ${readyToReserve.length} unit${readyToReserve.length === 1 ? '' : 's'} for the window below. Optional — a step can run without a hold.`
      : !expectedStartIso && (missingUnit < equipmentReqs.length || equipmentReqs.length === 0)
        ? 'Set the expected start at the top of this dialog to reserve equipment.'
        : noRunTime > 0
          ? `${noRunTime} selected unit${noRunTime === 1 ? ' has' : 's have'} no run time configured, so there is no window to hold.`
          : missingUnit > 0
            ? 'Pick a unit, and set the expected start above, to reserve it.'
            : 'Nothing to reserve.'

  const handleReserve = () => {
    if (!selections || readyToReserve.length === 0) return
    const items: ReserveEquipmentItem[] = []
    for (const r of readyToReserve) {
      // The BOOKING is made at the step start; the backend applies prep itself,
      // so sending the prep-shifted time would add it twice. `derivedWindow` is
      // for showing and for asking about availability, not for the payload.
      //
      // An overridden row sends `wantedAt` ALONGSIDE the step start rather than
      // instead of it: the server books at `wantedAt` with no prep, and the
      // record still carries the step plan the row departed from.
      const wantedAt = localToIso(startOverrides?.[r.id] ?? '')
      if (!expectedStartIso && !wantedAt) continue
      items.push({
        requirementId: r.id,
        equipmentId: selections[r.id],
        startsAt: expectedStartIso ?? (wantedAt as string),
        ...(wantedAt ? { wantedAt } : {}),
      })
    }
    if (items.length > 0) onReserve?.(items)
  }

  // "Reserve" for inventory mirrors equipment's own: a real hold placed the
  // moment it's clicked, not a local auto-fill — before Start is even signed
  // (z8nrz7dyfp follow-up). Only mode 'start' offers it: in 'end'/'complete'
  // every lot named is already an execution's recorded history, not a fresh
  // pick to commit.
  const inventoryReqs = connection.inventoryRequirements
  const inventoryReserveLines =
    mode === 'start'
      ? inventoryReqs.flatMap(r => {
          const picked = lotAllocations?.[r.id] ?? {}
          const already = reservedInventory?.[r.id] ?? {}
          return Object.entries(picked)
            .map(([lot, qtyStr]) => ({
              requirementId: r.id,
              lot,
              // The TOTAL this step should hold on the lot, never an increment.
              // The server reconciles against its own open holds and writes
              // only the shortfall, so a pick submitted twice is a no-op there.
              // Sending a delta instead made correctness depend on
              // `reservedInventory`, which is local state a modal remount
              // clears — reopen, press Reserve, and the whole quantity was
              // reserved a second time on top of the first.
              qty: Number(qtyStr),
              // What is still outstanding, for the BUTTON only: a row already
              // fully held should not make Reserve look like it has work to do.
              outstanding: Number(qtyStr) - (already[lot] ?? 0),
            }))
            .filter(l => Number.isFinite(l.qty) && l.outstanding > 1e-9)
        })
      : []
  const allInventoryReserved =
    inventoryReqs.length > 0 &&
    mode === 'start' &&
    inventoryReqs.every(r => {
      const picked = lotAllocations?.[r.id] ?? {}
      const already = reservedInventory?.[r.id] ?? {}
      return Object.entries(picked).every(
        ([lot, qtyStr]) => Number(qtyStr) <= (already[lot] ?? 0) + 1e-9,
      )
    }) &&
    inventoryReqs.some(r => Object.keys(lotAllocations?.[r.id] ?? {}).length > 0)
  const inventoryReserveHint = allInventoryReserved
    ? 'Every ticked lot is reserved.'
    : inventoryReserveLines.length > 0
      ? `Holds ${inventoryReserveLines.length} lot${inventoryReserveLines.length === 1 ? '' : 's'} for this step. Optional — a step can start without one.`
      : 'Tick lots below, then Reserve to hold them before you sign Start.'

  const handleReserveInventoryClick = () => {
    if (inventoryReserveLines.length === 0) return
    const byRequirement = new Map<string, ReserveLine[]>()
    for (const line of inventoryReserveLines) {
      const arr = byRequirement.get(line.requirementId) ?? []
      arr.push({ lot: line.lot, qty: line.qty })
      byRequirement.set(line.requirementId, arr)
    }
    onReserveInventory?.(
      [...byRequirement.entries()].map(([requirementId, lines]) => ({ requirementId, lines })),
    )
  }

  return (
    <div className='space-y-3'>
      <SectionLabel>Configured process</SectionLabel>
      <Tabs defaultValue={defaultTab}>
        <TabsList className='h-auto w-full min-w-0 justify-start gap-0.5 overflow-x-auto p-1'>
          {TAB_DEFS.map(t => (
            <TabsTrigger key={t.value} value={t.value} className='gap-1.5 text-xs'>
              {t.label}
              {/* Progress where there is progress to report, size otherwise.
                  "2/6" answers what an operator is actually asking of a tab
                  they have not opened yet; "6" only says how much is in it. */}
              <Badge
                variant='secondary'
                className={cn(
                  'px-1.5 font-mono text-[10px] tabular-nums',
                  t.filled !== null &&
                    t.filled === t.count &&
                    'bg-green-100 text-green-800 hover:bg-green-100',
                )}
              >
                {t.filled !== null ? `${t.filled}/${t.count}` : t.count}
              </Badge>
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value='equipment' className='mt-2'>
          {/* Reserve books EQUIPMENT and nothing else. It used to sit beside the
              tab strip, above whichever tab was open, so on the Inventory tab it
              read as a control over the lots below it — "reserve what I entered"
              — when it has never had anything to do with them. It belongs with
              the rows it acts on. */}
          {equipmentOn && counts.equipment > 0 && selections && onSelectEquipment && (
            <div className='mb-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border border-gray-200 bg-gray-50/70 px-3 py-2'>
              <Button
                type='button'
                disabled={readyToReserve.length === 0}
                onClick={handleReserve}
                className='h-8 shrink-0 px-5 text-xs font-semibold'
              >
                {allReserved
                  ? 'All reserved'
                  : readyToReserve.length > 0
                    ? `Reserve (${readyToReserve.length})`
                    : 'Reserve'}
              </Button>
              <p
                className={cn(
                  'min-w-0 flex-1 text-[11px]',
                  allReserved ? 'text-green-700' : 'text-gray-500',
                )}
              >
                {reserveHint}
              </p>
            </div>
          )}
          {/* Without the feature, useEquipmentTypes/Units 403 and resolve to [],
              so type names collapse to raw ids and every unit dropdown is empty.
              That is indistinguishable from "nothing configured" — say why. */}
          {!equipmentOn ? (
            <FeatureDisabledNote label='Equipment' className='my-2' />
          ) : counts.equipment === 0 ? (
            <p className='py-3 text-xs italic text-gray-400'>No equipment configured.</p>
          ) : (
            <ul className='space-y-3'>
              {connection.equipmentRequirements.map(r => {
                const chosen = assignedByRequirement.get(r.id)
                const held = reservationByRequirement.get(r.id)
                const options = unitsByType.get(r.equipmentTypeId) ?? []

                // What the picker may offer, how many of the fleet are free at
                // the planned window, and why the rest were withheld — all from
                // one pure, tested module (`src/lib/process/unitOptions.ts`),
                // because vitest cannot run a `.tsx` and this decides what an
                // operator is allowed to pick.
                //
                // Computed here rather than beside the other row derivations
                // below: `held` is read by it and is declared just above.

                // The row's state, named once and used for the dot, the pill and
                // the card's border. Deriving it in three places was how the
                // green banner ended up disagreeing with the times beside it.
                // The unit this row has landed on, whichever way it got there —
                // a live hold, or a selection recorded at Start. Resolved to a
                // label here so the header can name it without three fallbacks
                // inline.
                const assignedUnitId = held?.equipmentId ?? chosen?.equipmentId ?? selections?.[r.id]
                const assignedUnitRow = assignedUnitId
                  ? options.find(u => u.id === assignedUnitId)
                  : undefined
                const assignedUnit = assignedUnitId
                  ? {
                      label: assignedUnitRow?.label ?? chosen?.equipmentLabel ?? assignedUnitId,
                      site: assignedUnitRow?.place ?? assignedUnitRow?.locationSite ?? null,
                    }
                  : null

                const rowState: 'verified' | 'reserved' | 'assigned' | 'empty' = held?.actualStart
                  ? 'verified'
                  : held
                    ? 'reserved'
                    : selections?.[r.id] || chosen
                      ? 'assigned'
                      : 'empty'

                // A unit cannot be picked before there is a time to pick it FOR.
                // Availability is answered for a specific window, so without one
                // the list would be "everything", and the operator would choose
                // against no constraint and be refused at Reserve instead.
                const hasTime = !!expectedStartIso || !!startOverrides?.[r.id]

                // ONLY WHAT IS ACTUALLY FREE. Busy units used to stay in the
                // list, on the reasoning that the operator might be planning a
                // different window — but the window IS chosen by the time this
                // opens, so that reasoning no longer holds and an offered unit
                // that Reserve then refuses is worse than one not offered.
                // The count of what was withheld is stated below, so a short
                // list never reads as a broken one.
                const { selectable, freeCount, hiddenCount, cappedCount, withheldNote } =
                  partitionUnitOptions(
                    options,
                    availabilityFor ? unitId => availabilityFor(r.id, unitId) : null,
                    held?.equipmentId ?? null,
                  )

                // Lifted out of the markup so it can sit beside the name it
                // answers — "which unit" belongs next to "which equipment",
                // not three blocks below it.
                const unitPicker =
                  selections && onSelectEquipment ? (
                    <Select
                      value={held ? held.equipmentId : (selections[r.id] ?? '')}
                      onValueChange={v => onSelectEquipment(r.id, v)}
                      disabled={!!held || !hasTime}
                    >
                      <SelectTrigger
                        title={
                          held
                            ? 'Reserved — release the hold to pick a different unit'
                            : !hasTime
                              ? 'Set the expected start first — units are offered for that window'
                              : undefined
                        }
                        className={cn(
                          'h-8 text-sm',
                          held || !hasTime ? 'bg-gray-100 text-gray-600' : 'bg-white',
                          hasTime &&
                            !held &&
                            !selections[r.id] &&
                            'border-amber-300 text-muted-foreground',
                        )}
                      >
                        <SelectValue
                          placeholder={
                            !hasTime
                              ? 'Set the expected start first'
                              : selectable.length === 0
                                ? 'No units free for this window'
                                : 'Select a unit…'
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {selectable.map(u => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.label}
                            {(u.place ?? u.locationSite) ? ` · ${u.place ?? u.locationSite}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : chosen ? (
                    <p className='flex items-center gap-1.5 text-xs text-gray-600'>
                      <CheckCircle2 className='h-3.5 w-3.5 text-green-600' />
                      {chosen.equipmentLabel}
                    </p>
                  ) : null

                return (
                  <li
                    key={r.id}
                    // ONE CARD PER REQUIREMENT, read top to bottom: what
                    // equipment, which unit, when. It used to be two bordered
                    // panels side by side with the equipment's NAME buried in
                    // the right-hand one — so the first thing an operator looks
                    // for was the last thing they found, and neither panel was
                    // clearly the primary one.
                    className={cn(
                      'rounded-lg border p-3 transition-colors',
                      rowState === 'verified'
                        ? 'border-green-300 bg-green-50/40'
                        : rowState === 'reserved'
                          ? 'border-green-200 bg-white'
                          : 'border-gray-200 bg-white',
                    )}
                  >
                    {/* A booking made before the unit went out of service
                        stands; it is not cancelled (z8nrz7fkgd). Said here,
                        where the booking is, so nobody walks to a machine
                        that is down expecting to use it. */}
                    {held && assignedUnitRow?.status === 'OUT_OF_SERVICE' && (
                      <p
                        role='status'
                        className='mb-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800'
                      >
                        <span className='font-semibold'>{assignedUnit?.label ?? 'This unit'} is out of service.</span>{' '}
                        The booking stands, but the unit takes no new bookings until it is back in service.
                      </p>
                    )}
                    {/* WHAT, AND WHICH ONE — side by side, because the picker
                        answers the name beside it. */}
                    <div className='flex flex-wrap items-start justify-between gap-x-3 gap-y-2'>
                      <div className='flex min-w-0 flex-1 items-start gap-2.5'>
                        <span
                          aria-hidden
                          className={cn(
                            'mt-1 block h-2.5 w-2.5 shrink-0 rounded-full border-2',
                            rowState === 'verified'
                              ? 'border-green-600 bg-green-600'
                              : rowState === 'reserved'
                                ? 'border-green-600 bg-white'
                                : rowState === 'assigned'
                                  ? 'border-gray-400 bg-white'
                                  : 'border-gray-300 bg-white',
                          )}
                        />
                        <div className='min-w-0'>
                          <p className='truncate text-sm font-medium text-gray-900'>
                            {typeName(r.equipmentTypeId)}
                          </p>
                          {/* The unit's own identity when there is one, the
                              shape of the job when there is not. */}
                          <p className='mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[11px] text-gray-500'>
                            {assignedUnit && (
                              <>
                                <span className='font-mono font-medium text-gray-700'>
                                  {assignedUnit.label}
                                </span>
                                {assignedUnit.site && (
                                  <>
                                    <span aria-hidden className='text-gray-300'>
                                      ·
                                    </span>
                                    <span>{assignedUnit.site}</span>
                                  </>
                                )}
                                <span aria-hidden className='text-gray-300'>
                                  ·
                                </span>
                              </>
                            )}
                            <span className='tabular-nums'>
                              {(r.setupHrs + r.runHrs + r.unloadHrs).toFixed(1)} h/run
                            </span>
                            {!held && !chosen && options.length > 0 && (
                              <>
                                <span aria-hidden className='text-gray-300'>
                                  ·
                                </span>
                                <span className='tabular-nums'>
                                  {availabilityFor
                                    ? `${freeCount} of ${options.length} free`
                                    : `${options.length} unit${options.length === 1 ? '' : 's'}`}
                                </span>
                                {/* Says WHY the dropdown is shorter than the
                                    fleet. Without it a list of one, from a type
                                    the operator knows has six, reads as a bug. */}
                                {hiddenCount > 0 && (
                                  <span
                                    className='text-amber-700'
                                    title={
                                      cappedCount > 0
                                        ? "Not offered: units booked or in maintenance for this window, and units that have already run their configured number of times today — those are free now but cannot take another run until tomorrow. Raise Runs / day on the form builder's Process tab to allow more."
                                        : 'Units already booked or in maintenance for this window are not offered'
                                    }
                                  >
                                    ({withheldNote})
                                  </span>
                                )}
                              </>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className='w-full shrink-0 sm:w-[230px]'>{unitPicker}</div>
                    </div>

                    {/* PLAN, THEN WHAT HAPPENED — the three read top to bottom in
                        the order they occur.

                        Expected Start DEFAULTS to derived — the step's one
                        expected start plus this requirement's prep — and stays
                        live as the shared start moves, so rows agree with the run
                        by default. The picker detaches one row from it when the
                        operator needs a machine at a time the plan does not
                        produce; Reset puts it back. Actual Start and End come
                        from the hold and only exist once there is one — before
                        that there is nothing to have happened yet, and rendering
                        empty labels would be three dashes of noise on every
                        unreserved row. */}
                    {/* WHEN — the plan and what became of it, in one strip. The
                        times used to be split between this block and a green
                        "Reserved 11:04 → 11:19" banner in the panel opposite,
                        which meant the same window was printed twice and could
                        be read as two different facts. */}
                    {/* items-START, not items-end. Bottom-aligning let the
                        Expected start block — which is a date PICKER on an
                        editable row and a line of text on a locked one — push
                        its own label to a different height than the two beside
                        it, so no two rows had their labels on one baseline. */}
                    <div className='mt-2.5 flex flex-wrap items-start justify-between gap-x-4 gap-y-2 rounded-md border border-gray-200 bg-gray-50/70 px-3 py-2'>
                      <div className='flex min-w-0 flex-wrap items-start gap-x-5 gap-y-2'>
                      <div className='min-w-0'>
                      <p className='text-[10px] font-medium uppercase tracking-wide text-gray-500'>
                        Expected start
                      </p>
                      {(() => {
                        const overridden = !!startOverrides?.[r.id]
                        const win = derivedWindow(r)
                        // A held row shows the booked window — that is the truth
                        // about the machine. An unheld one shows what Reserve
                        // would ask for.
                        const shown = held ? held.startsAt : (win?.from ?? null)
                        const editable =
                          !!onOverrideStart && (!held || (!!onRetimeRow && !held.actualStart))
                        const pending = retimingRow === r.id

                        if (!editable) {
                          const at = shown ?? chosen?.expectedStart ?? fallbackStart ?? null
                          return at ? (
                            <>
                              {/* CLOCK ON THE VALUE LINE, date beneath. The full
                                  stamp ("9/5/2026, 8:33:00 AM") sat beside bare
                                  clocks like "08:29 AM" — three times in two
                                  formats and three widths, so the columns could
                                  not line up and the seconds added nothing. */}
                              <p className='font-mono text-sm tabular-nums text-gray-900'>
                                {clockOf(at)}
                              </p>
                              <p className='whitespace-nowrap text-[10px] tabular-nums text-gray-400'>
                                {dayOf(at)}
                              </p>
                            </>
                          ) : (
                            <p className='font-mono text-sm tabular-nums text-gray-300'>--:--</p>
                          )
                        }

                        return (
                          <>
                            <Input
                              type='datetime-local'
                              // Reflects the row's real time whether that came
                              // from an override, a booking, or the shared start
                              // — so the picker never shows something other than
                              // the value beside it.
                              value={
                                startOverrides?.[r.id] ?? (shown ? isoToLocalInput(shown) : '')
                              }
                              onChange={e =>
                                held
                                  ? onRetimeRow?.(r.id, e.target.value)
                                  : onOverrideStart(r.id, e.target.value)
                              }
                              disabled={pending}
                              aria-label={`Expected start for ${typeName(r.equipmentTypeId)}`}
                              title={
                                held
                                  ? 'Changing this moves the booking to the new time'
                                  : 'Sets exactly when this unit is wanted — prep is not added on top'
                              }
                              // FIXED so the column lines up across rows — a
                              // flexing picker made Expected start two or three
                              // times the width of the two beside it, which is
                              // most of why the strip never looked like one row.
                              //
                              // 240, not 200. `datetime-local` renders its own
                              // calendar button INSIDE the box, and its widest
                              // state is the empty one — the placeholder
                              // "dd/mm/yyyy, --:-- --" is longer than any value
                              // it will ever hold. At 200 that button was
                              // clipped by the field's own edge on every
                              // unfilled row. The header field below carries the
                              // same note; they must not drift apart.
                              className='h-8 w-[240px] max-w-full bg-white text-xs'
                            />
                            {pending ? (
                              <p className='text-[11px] text-blue-700'>Moving the hold…</p>
                            ) : overridden ? (
                              <p className='flex items-center gap-1.5 text-[11px] text-gray-500'>
                                Set manually
                                <button
                                  type='button'
                                  onClick={() => onOverrideStart(r.id, '')}
                                  className='font-medium text-blue-600 underline-offset-2 hover:underline'
                                >
                                  Reset
                                </button>
                              </p>
                            ) : (
                              // Only meaningful while the row is still derived —
                              // an overridden row's time owes nothing to prep.
                              r.prepHrs > 0 && (
                                <p className='text-[11px] text-gray-500'>
                                  {r.prepHrs}h prep after the step start
                                </p>
                              )
                            )}
                            {rowRetimeError?.id === r.id && (
                              <p role='alert' className='text-[11px] leading-snug text-red-700'>
                                {rowRetimeError.message} — the hold did not move.
                              </p>
                            )}
                          </>
                        )
                      })()}

                      </div>

                      {/* ACTUAL START AND END, beside the plan rather than
                          under it — they are what the plan is measured against.
                          Only once a hold exists: before that there is nothing
                          to have happened yet, and three empty labels on every
                          unbooked row is noise. */}
                      {held && (
                        <>
                          {/* Fixed widths, so the three columns line up ACROSS
                              rows and not just within one. A clock is a known
                              width; letting these size to their content meant a
                              row whose end had rolled past midnight sat wider
                              than the row above it. */}
                          <div className='w-[112px] shrink-0'>
                            <p className='text-[10px] font-medium uppercase tracking-wide text-gray-500'>
                              Actual start
                            </p>
                            {held.actualStart ? (
                              <>
                                <p className='font-mono text-sm tabular-nums text-gray-900'>
                                  {clockOf(held.actualStart)}
                                </p>
                                {/* The day, because a run can start after
                                    midnight on the day after it was planned. */}
                                <p className='whitespace-nowrap text-[10px] tabular-nums text-gray-400'>
                                  {dayOf(held.actualStart)}
                                </p>
                              </>
                            ) : (
                              // "Nobody has been to this machine" is a different
                              // fact from "this has not ended", so it gets a
                              // placeholder shaped like a time rather than a dash.
                              <p
                                className='font-mono text-sm tabular-nums text-gray-300'
                                title='Not in use yet — scan this unit, or press Start unit'
                              >
                                --:--
                              </p>
                            )}
                          </div>
                          <div className='w-[112px] shrink-0'>
                            <p className='text-[10px] font-medium uppercase tracking-wide text-gray-500'>
                              End
                            </p>
                            <p
                              className={cn(
                                'font-mono text-sm tabular-nums',
                                held.actualEnd ? 'text-gray-900' : 'text-gray-400',
                              )}
                            >
                              {clockOf(held.actualEnd ?? held.endsAt)}
                            </p>
                            {/* Day first, then whether it happened. A long run
                                ends on a different date than it started, and a
                                bare "08:48" beside an 08:29 start reads as a
                                nineteen-minute run when it may be a day. */}
                            <p className='whitespace-nowrap text-[10px] tabular-nums text-gray-400'>
                              {dayOf(held.actualEnd ?? held.endsAt)}
                            </p>
                            {/* Its OWN line, not appended to the date: with the
                                year always shown, "Sep 5, 2026 · projected" no
                                longer fits the column without wrapping.
                                The booked end is a PROJECTION until the step is
                                ended, and saying so is the difference between a
                                time that happened and one merely expected. */}
                            {!held.actualEnd && (
                              <p className='text-[10px] italic text-gray-400'>projected</p>
                            )}
                          </div>
                        </>
                      )}
                      </div>

                      {/* THE ROW'S STATE, said once. Assigned / Reserved / In
                          use are the same words the header counters use, so the
                          tally above and the row below cannot appear to
                          disagree. Release sits with it because releasing is
                          what undoes the state it names. */}
                      <div className='flex shrink-0 items-center gap-2'>
                        <span
                          className={cn(
                            'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium',
                            rowState === 'verified'
                              ? 'bg-green-100 text-green-800'
                              : rowState === 'reserved'
                                ? 'bg-green-50 text-green-800'
                                : rowState === 'assigned'
                                  ? 'bg-blue-50 text-blue-800'
                                  : 'bg-gray-100 text-gray-500',
                          )}
                        >
                          {rowState === 'verified' && (
                            <CheckCircle2 className='h-3 w-3 shrink-0 text-green-600' />
                          )}
                          {/* "In use", not "Verified". Verification is the
                              MECHANISM that recorded it — a scan, or the button
                              beside this — while the state it produces is that
                              the operator is on the machine now. The trail keeps
                              the mechanism (`scan_verified` vs
                              `started_manually`); the screen states the fact. */}
                          {rowState === 'verified'
                            ? 'In use'
                            : rowState === 'reserved'
                              ? 'Reserved'
                              : rowState === 'assigned'
                                ? 'Assigned'
                                : 'Not assigned'}
                        </span>
                        {/* START THIS UNIT BY HAND. The scan below proves the
                            operator was at the machine; this does not, and the
                            server records the difference (`started_manually` vs
                            `scan_verified`). It exists because a label can be
                            damaged or a scanner absent, and a run that happened
                            with no recorded start is worse than one recorded
                            without a scan. Gone once started — the timestamp
                            cannot move, so offering it again would invite an
                            action with no effect. */}
                        {held && !held.actualStart && onStartUnit && (
                          <button
                            type='button'
                            onClick={() => onStartUnit(held.id)}
                            disabled={startingUnit === held.id}
                            title='Mark this unit started now — records the actual start without a scan'
                            className='inline-flex shrink-0 items-center gap-1 rounded-full border border-green-300 bg-white px-2 py-0.5 text-[11px] font-medium text-green-700 transition-colors hover:bg-green-50 disabled:opacity-50'
                          >
                            <CheckCircle2 className='h-3 w-3 shrink-0' />
                            {/* "Start unit", never bare "Start": the dialog's
                                primary action is Start Process Step, and two
                                controls reading "Start" a few inches apart do
                                different things — one signs a transition, one
                                says "I am at this machine now". */}
                            {startingUnit === held.id ? 'Starting…' : 'Start unit'}
                          </button>
                        )}
                        {held && onRelease && (
                          <button
                            type='button'
                            onClick={() => onRelease(held.id)}
                            className='shrink-0 text-[11px] font-medium text-blue-600 underline-offset-2 hover:underline'
                          >
                            Release
                          </button>
                        )}
                      </div>
                    </div>

                    <div className='mt-2'>
                      {/* SCANNING DOES TWO JOBS, and which one depends on whether
                          the row is held.

                          UNHELD: it fills the row, proving the unit is the one
                          this requirement asks for — which picking from the
                          dropdown below cannot. The dropdown stays for a damaged
                          or missing label, and the server applies the SAME type
                          gate to both, so neither route can book the wrong
                          machine.

                          HELD: it VERIFIES. The window below was a plan; scanning
                          at the bench is the operator saying "I am starting on
                          this unit now", and stamps the Actual Start above. This
                          used to be hidden once a row was held, on the reasoning
                          that a booked row had nothing left to verify — but the
                          verification is the point, and the plan is exactly what
                          it is measured against. Scanning a DIFFERENT unit into a
                          held row is still refused by the server; Release remains
                          the way to change which machine is booked.

                          Hidden once verified: re-scanning cannot move the
                          timestamp, so offering the field again would invite an
                          action with no effect. */}
                      {onScan && !held?.actualStart && (
                        <EquipmentScanField
                          onScan={code => onScan(r.id, code)}
                          pending={scanPendingFor === r.id}
                          verifyingHold={!!held}
                          // Only this row's verdict. The refusal names the row it
                          // was raised for, so a scan into the centrifuge row
                          // cannot paint an error onto the apheresis row.
                          refusal={scanRefusal?.requirementId === r.id ? scanRefusal : null}
                          lastVerified={
                            lastScanVerified?.requirementId === r.id
                              ? lastScanVerified.label
                              : null
                          }
                        />
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </TabsContent>

        <TabsContent value='inventory' className='mt-2'>
          {/* Lot allocation reads the materials master, so with inventory off the
              allocator renders with nothing to pick. */}
          {!inventoryOn ? (
            <FeatureDisabledNote label='Inventory' className='my-2' />
          ) : counts.inventory === 0 ? (
            <p className='py-3 text-xs italic text-gray-400'>No inventory configured.</p>
          ) : (
            <>
              {/* Mirrors the equipment tab's own Reserve button — belongs with
                  the rows it acts on, same reasoning as that one. Only mode
                  'start' offers it: end/complete show recorded history, not a
                  fresh pick. */}
              {mode === 'start' && lotAllocations && onAllocateLot && onReserveInventory && (
                <div className='mb-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border border-gray-200 bg-gray-50/70 px-3 py-2'>
                  <Button
                    type='button'
                    disabled={inventoryReserveLines.length === 0}
                    onClick={handleReserveInventoryClick}
                    className='h-8 shrink-0 px-5 text-xs font-semibold'
                  >
                    {allInventoryReserved
                      ? 'All reserved'
                      : inventoryReserveLines.length > 0
                        ? `Reserve (${inventoryReserveLines.length})`
                        : 'Reserve'}
                  </Button>
                  <p
                    className={cn(
                      'min-w-0 flex-1 text-[11px]',
                      allInventoryReserved ? 'text-green-700' : 'text-gray-500',
                    )}
                  >
                    {inventoryReserveHint}
                  </p>
                </div>
              )}

              {/* Only worth offering when there is more than one to move. */}
              {lotAllocations && onAllocateLot && counts.inventory > 1 && (
                <div className='mb-1.5 flex items-center justify-between'>
                  <p className='text-[11px] text-gray-500'>
                    {counts.inventory} materials to allocate
                  </p>
                  <button
                    type='button'
                    onClick={() => setAllInventoryOpen(!allInventoryExpanded)}
                    className='text-[11px] font-medium text-primary underline-offset-2 hover:underline'
                  >
                    {allInventoryExpanded ? 'Collapse all' : 'Expand all'}
                  </button>
                </div>
              )}
            <ul className='space-y-1.5'>
              {connection.inventoryRequirements.map(r => {
                if (lotAllocations && onAllocateLot) {
                  const short = shortProposals?.get(r.id)
                  return (
                    <div key={r.id} className='space-y-1'>
                      <MaterialLotAllocator
                        submissionId={submissionId}
                        fieldId={fieldId}
                        mode={mode}
                        requirement={r}
                        allocations={lotAllocations[r.id] ?? {}}
                        onAllocate={(lot, value, commit) =>
                          onAllocateLot(r.id, lot, value, commit)
                        }
                        onAllocateMany={
                          onAllocateLotMany
                            ? updates => onAllocateLotMany(r.id, updates)
                            : undefined
                        }
                        reservedByLot={reservedInventory?.[r.id]}
                        blockExpired={connection.isKitStep}
                        quantityMultiplier={connection.quantityMultiplier}
                        lockQuantity={connection.isKitStep}
                        open={isInventoryOpen(r.id)}
                        onToggle={() => toggleInventory(r.id)}
                      />
                      {/* Name the outstanding quantity rather than quietly
                          proposing less — a short pick the operator cannot see
                          becomes a Start button that will not light, with
                          nothing on screen saying why. */}
                      {short && (
                        <p className='rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-900'>
                          Free stock covers {short.qtyRequired - short.shortfall} of{' '}
                          {short.qtyRequired} — {short.shortfall} short.
                          {short.alerts.length > 0 && ` ${short.alerts[0].message}`}
                        </p>
                      )}
                    </div>
                  )
                }
                const label = r.itemLabel ?? r.consumableId
                const chosen = assignedInventoryByRequirement.get(r.id)
                return (
                  <li
                    key={r.id}
                    className='space-y-1.5 rounded-md border border-gray-200 bg-gray-50/70 px-3 py-2'
                  >
                    <div className='flex items-center justify-between text-sm text-gray-800'>
                      <span className='truncate font-medium'>{label}</span>
                      <span className='ml-2 shrink-0 text-xs tabular-nums text-gray-500'>
                        required {r.qtyRequired}
                        {r.uom ? ` ${r.uom}` : ''}
                        {batchHint(r, connection.quantityMultiplier) && (
                          <span className='ml-1.5 text-[11px] text-gray-400'>
                            ({batchHint(r, connection.quantityMultiplier)})
                          </span>
                        )}
                      </span>
                    </div>
                    {chosen ? (
                      <div className='space-y-0.5'>
                        {chosen.lots.map(l => (
                          <p
                            key={l.lot}
                            className='flex items-center gap-1.5 text-xs text-gray-600'
                          >
                            <CheckCircle2 className='h-3.5 w-3.5 text-green-600' />
                            {l.lot} × {l.qty}
                          </p>
                        ))}
                      </div>
                    ) : null}
                  </li>
                )
              })}
            </ul>
            </>
          )}
        </TabsContent>

        <TabsContent value='staffing' className='mt-2'>
          {counts.staffing === 0 ? (
            <p className='py-3 text-xs italic text-gray-400'>No staffing configured.</p>
          ) : (
            <ul className='space-y-1'>
              {connection.staffingLines.map(l => (
                <li
                  key={l.id}
                  className='flex items-center justify-between rounded-md border border-gray-200 bg-gray-50/70 px-3 py-1.5 text-sm text-gray-800'
                >
                  <span className='truncate'>{l.roleLabel ?? l.staffingRoleId ?? 'Role'}</span>
                  <span className='ml-2 shrink-0 text-xs tabular-nums text-gray-500'>
                    ×{l.numResources} · {(l.prepHrs + l.setupHrs + l.runHrs + l.unloadHrs).toFixed(1)} h
                  </span>
                </li>
              ))}
            </ul>
          )}
        </TabsContent>

        <TabsContent value='attributes' className='mt-2'>
          {counts.attributes === 0 ? (
            <p className='py-3 text-xs italic text-gray-400'>No attributes configured.</p>
          ) : (
            <div className='flex flex-wrap gap-1.5'>
              {attributes.map(([key, value]) => (
                <span
                  key={key}
                  className='rounded-md border border-gray-200 bg-gray-50/70 px-2 py-0.5 text-xs text-gray-700'
                >
                  {key}: {value}
                </span>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

// A reserve failure is either a rich 409 conflict (with a suggested free slot)
// or any other error (400/404/timeout/deadlock-abort/etc.) that only carries a
// plain message — both render in the same banner.
type ReserveError =
  | { kind: 'conflict'; body: ReservationConflict }
  | { kind: 'generic'; message: string }

// ─── Signed transition modal ─────────────────────────────────────────────────

/** Unsigned Start-mode input, held by the card so closing the dialog keeps it. */
export interface StartDraft {
  selections: Record<string, string>
  lotAllocations: Record<string, Record<string, string>>
  /**
   * The step's expected start, as a `datetime-local` string.
   *
   * Held by the CARD rather than the modal because it is offered in both places
   * — the operator can set it on the row before ever opening the dialog — and
   * two components editing one value must not each keep their own copy of it.
   */
  expectedStart?: string
}

function ProcessStepActionModal({
  field,
  processFieldId,
  mode,
  connection,
  execution,
  submissionId,
  scheduledStart = null,
  kit,
  onClose,
  onStarted,
  onTransitioned,
  startDraft,
  onStartDraftChange,
}: {
  field: FieldDefinition
  /** The connection/execution key — the Field Library id when the field was inserted from it. */
  processFieldId: string
  /** null = closed */
  mode: TransitionMode | null
  connection: ProcessStepConnection
  /** The current execution, when one exists — carries the units assigned at start. */
  execution?: ProcessStepExecution
  submissionId: string
  /**
   * The procedure's scheduled start, as an ISO instant. Seeds the expected
   * start when nothing else has set one, so a scheduled run does not open to
   * an empty field. Null for an ad-hoc form or an unscheduled procedure.
   */
  scheduledStart?: string | null
  /**
   * Kit identity and its derived expiry, restated on the confirm summary.
   * Derived by the card (from the lots on screen), so it is passed in rather
   * than recomputed here and risking the two disagreeing.
   */
  kit?: { supplyId: string | null; expiry: string | null } | null
  onClose: () => void
  /** Start succeeded — hand the modal to the mid-run working screen. */
  onStarted: () => void
  /**
   * A signed transition landed. Distinct from `onClose`, which also fires on
   * cancel and on dismissing the dialog — only a real transition should make
   * the host re-read the submission.
   */
  onTransitioned: () => void
  /**
   * Allocations entered in Start mode but not yet signed for.
   *
   * Owned by the card, not this modal, because the modal unmounts on close and
   * took the operator's work with it: they filled in three materials, closed
   * the dialog, reopened it and found every box empty. Nothing is written to
   * the server until Start is signed — a hold placed by an unsigned step is not
   * a decision anyone made — so the draft has to live somewhere that outlives
   * the dialog.
   */
  startDraft: StartDraft | null
  onStartDraftChange: (draft: StartDraft) => void
}) {
  const { email, user } = useAuth()
  const signer = email ?? '' // credential identity — used for verify + audit
  const signerName = user?.fullName || email || '' // display name (never the id/username)

  const transition = useProcessStepTransition()
  // Reservations are start-mode only — no live holds to show/act on otherwise.
  const { data: reservations } = useProcessStepReservations(
    mode && mode !== 'complete' ? submissionId : null,
    processFieldId,
  )
  const reserveEquipment = useReserveEquipment()
  const reserveInventory = useReserveInventory()
  const returnInventory = useReturnInventory()
  // `returnInventory` (unlike `reserveInventory`) names a lot by its
  // InventoryLot DB id, not the human lot number `lotAllocations` is keyed
  // by — this is what resolves one to the other when a tick is undone below.
  // Only the materials this step is configured with (z8nrz7e8kw), and only
  // while the modal is open: it is mounted for every configured field on the
  // form, so asking while closed cost one materials request per field.
  const { lotsFor } = useProcessMaterialLots(
    mode ? connection.inventoryRequirements.map(r => r.consumableId) : [],
  )
  const scanEquipment = useScanEquipment()
  const releaseReservation = useReleaseReservation()
  const startReservation = useStartReservation()
  const retimeReservations = useRetimeReservations()
  const updateSelections = useUpdateSelections()

  // The shared start locks once everything is held, because a plain input there
  // would silently disagree with the windows already booked. `retiming` is the
  // way back: an explicit edit whose confirm moves the holds too, so the field
  // and the bookings are never allowed to drift apart.
  const [retiming, setRetiming] = useState(false)
  const [retimeDraft, setRetimeDraft] = useState('')
  const [retimeError, setRetimeError] = useState<string | null>(null)
  /** The confirm dialog is open — Apply asks before it moves anything. */
  const [retimeAsking, setRetimeAsking] = useState(false)

  /**
   * Per-row expected starts the operator set by hand, keyed by requirement id.
   *
   * Absent means the row is still DERIVED — the step's start plus that row's
   * prep — which is the default and stays live as the shared start moves. An
   * entry detaches that one row from it until Reset. Held here rather than in
   * the tabs because Start's payload is built in this component and has to
   * record the same times the rows are showing.
   *
   * `datetime-local` strings, matching the picker; converted at the edges.
   */
  const [startOverrides, setStartOverrides] = useState<Record<string, string>>({})
  const [rowRetimeError, setRowRetimeError] = useState<{ id: string; message: string } | null>(
    null,
  )

  /** Which row's hold is being moved, so only that picker shows a pending state. */
  const [retimingRow, setRetimingRow] = useState<string | null>(null)

  // Resolvers for the confirm summary. Both queries are already loaded by
  // ConfiguredProcessTabs below; TanStack serves them from cache rather than
  // refetching, so naming them here costs nothing.
  // Only while open: the modal is mounted, closed, for every configured field.
  const { data: summaryEquipmentTypes } = useEquipmentTypes(!!mode)
  const { data: summaryEquipmentUnits } = useEquipmentUnits(!!mode)
  const equipmentTypeName = useMemo(() => {
    const map = new Map((summaryEquipmentTypes ?? []).map(t => [t.id, t.name]))
    return (id: string) => map.get(id) ?? id
  }, [summaryEquipmentTypes])
  const equipmentUnitName = useMemo(() => {
    const map = new Map((summaryEquipmentUnits ?? []).map(u => [u.id, u.label]))
    return (id: string) => map.get(id) ?? id
  }, [summaryEquipmentUnits])

  const [reserveError, setReserveError] = useState<ReserveError | null>(null)
  /** The server's verdict on the last scan. Null once it is superseded. */
  const [scanRefusal, setScanRefusal] = useState<ScanRefusal | null>(null)
  // Carries the row as well as the unit, so the green line appears on the row
  // that was actually filled rather than on every scan field at once.
  const [lastScanVerified, setLastScanVerified] = useState<{
    requirementId: string
    label: string
  } | null>(null)
  /** Which row's scan is in flight — see `scanPendingFor`. */
  const [scanningRequirementId, setScanningRequirementId] = useState<string | null>(null)
  /**
   * Phase two: the operator has pressed the action and is now signing for it.
   *
   * The signature bar used to sit open under the record from the moment the
   * dialog opened, which put the regulated act — typing your own name to attest
   * to something — on screen at the same time as the boxes that decide WHAT is
   * being attested to, with no order between them. Pressing the action first
   * and signing second is the order the work actually happens in, and it keeps
   * the working screen to the record alone.
   */
  const [signing, setSigning] = useState(false)
  // The password that IS the signature: re-entered here and exchanged for a
  // single-use token the server spends. See lib/signingToken.ts.
  const [signPassword, setSignPassword] = useState('')
  const [verifying, setVerifying] = useState(false)
  // `verifying` drives rendering, but it is state — two clicks (or a held
  // Enter) in the same tick both read it as false and fire two signed
  // transitions, each writing its own audit entry. The ref flips
  // synchronously, so the second caller is turned away before it can start.
  const confirming = useRef(false)
  const [signError, setSignError] = useState<string | null>(null)
  // Start mode: the unit picked per equipment requirement id.
  const [selections, setSelections] = useState<Record<string, string>>({})
  // Start mode: qty allocated per inventory requirement id → lot.
  const [lotAllocations, setLotAllocations] = useState<Record<string, Record<string, string>>>({})
  // Start mode: how much of each requirement's lot picks has actually been
  // committed server-side via the "Reserve" button (z8nrz7dyfp follow-up),
  // keyed the same way as `lotAllocations`. The DELTA between the two is what
  // Reserve still has to submit, and what an untick still has to release —
  // this is local bookkeeping of OUR OWN successful calls, not a live query,
  // the same trade-off `heldPicks`/`recorded` already make elsewhere in this
  // file for the same reason: no per-requirement "what's already reserved"
  // query is hoisted this high.
  const [reservedByReq, setReservedByReq] = useState<Record<string, Record<string, number>>>({})
  const [reserveInventoryError, setReserveInventoryError] = useState<string | null>(null)
  /** Whether this sitting has already taken its allocations from the live holds. */
  const holdsSeeded = useRef(false)

  /**
   * What each lot is believed to hold RIGHT NOW, including requests still in
   * flight — the ledger every reserve/release decision is measured against.
   *
   * A ref rather than state because the decisions are made inside input
   * handlers: React batches, so two keystrokes in one tick read the same
   * rendered value, and both released the same difference. This is written
   * synchronously, so the second sees the first.
   *
   * State is still updated alongside it, for rendering only.
   */
  const heldRef = useRef<Record<string, Record<string, number>>>({})
  const setHeld = useCallback((requirementId: string, lot: string, qty: number) => {
    const forReq = { ...(heldRef.current[requirementId] ?? {}), [lot]: qty }
    heldRef.current = { ...heldRef.current, [requirementId]: forReq }
    setReservedByReq(prev => ({ ...prev, [requirementId]: { ...prev[requirementId], [lot]: qty } }))
  }, [])

  /**
   * What the step ACTUALLY holds, merged with what this session just reserved.
   *
   * `reservedByReq` alone is a `useState` map of the calls this session happened
   * to make, so a page reload forgot every hold already placed: the stock was
   * still held, but the panel offered "Reserve (1)" again and the row's badge
   * demoted it to "allocated". The server answers that now.
   *
   * The session figure WINS where it exists, rather than the larger of the two.
   * It carries intent the server has not caught up with yet — a release fired on
   * a typed-down quantity lowers it immediately, while the refetch confirming it
   * is still in flight — and taking the maximum there would read back the
   * pre-release figure and fire the same release a second time.
   */
  // Only while open, for the same reason as the materials above: closed, this
  // was one request per inventory requirement per field on every form load.
  // Everything reading it is gated on `mode` already.
  const holdRequirementIds = useMemo(
    () => connection.inventoryRequirements.map(r => r.id),
    [connection.inventoryRequirements],
  )
  const serverHolds = useStepOpenHolds(mode ? submissionId : null, processFieldId, holdRequirementIds)
  const holdsFresh = useStepHoldsFresh(mode ? submissionId : null, processFieldId, holdRequirementIds)
  const reservedInventory = useMemo(() => {
    const merged: Record<string, Record<string, number>> = {}
    for (const requirementId of new Set([...Object.keys(serverHolds), ...Object.keys(reservedByReq)])) {
      const fromServer = serverHolds[requirementId] ?? {}
      const fromSession = reservedByReq[requirementId] ?? {}
      const byLot: Record<string, number> = {}
      for (const lot of new Set([...Object.keys(fromServer), ...Object.keys(fromSession)])) {
        byLot[lot] = fromSession[lot] ?? fromServer[lot] ?? 0
      }
      merged[requirementId] = byLot
    }
    return merged
  }, [serverHolds, reservedByReq])
  // Start mode: the step's ONE expected start, a `datetime-local` string here
  // and converted to ISO on submit. Blank = the server stamps now() for the
  // actual, and nothing is reserved — see the note on the confirm dialog.
  //
  // There is deliberately no per-requirement time any more. Every equipment
  // window is derived from this one value plus the step's longest staffing prep,
  // so a machine's booking cannot disagree with the run it belongs to.
  const [startedAt, setStartedAt] = useState('')
  // Undo start: a confirm panel that replaces the modal body, rather than a
  // nested <Dialog> — Radix dialog nesting fights over the focus trap, and the
  // swap reuses the signature input and the double-submit guard already here.
  const [undoing, setUndoing] = useState(false)
  const [undoReason, setUndoReason] = useState('')

  // "Sign as" (z8nrz7dt3z AC9): the transition may be signed by a colleague
  // under their own password while this session stays signed in. null = the
  // signed-in user. The roster loads only while a signature is on screen.
  const selfId = user?.userId ?? null
  const [signAsId, setSignAsId] = useState<string | null>(null)
  const signerRoster = useTenantSigners(PERMISSIONS.processExecute, signing || undoing)
  // Start, undo-start and a kit run's end are the assignee's alone — the
  // server's rule — so the picker marks everyone else rather than offering
  // them and letting the server refuse.
  const assigneeOnly = undoing || mode === 'start' || (mode === 'end' && connection.isKitStep)
  const signerOptions = useMemo(() => {
    const options = signerRoster.data ?? []
    return assigneeOnly ? restrictToAssignee(options, execution?.assigneeId ?? null) : options
  }, [signerRoster.data, assigneeOnly, execution?.assigneeId])
  const signerChoiceList = useMemo(() => signerChoices(signerOptions, selfId), [signerOptions, selfId])
  const chosenSignerId = signAsId ?? selfId
  const signingForOther = signAsId !== null && signAsId !== selfId
  const chosenName = chosenSignerName(chosenSignerId, signerChoiceList, selfId, signerName)
  const signerBlockedReason = signerBlocker('optional', chosenSignerId, signerChoiceList, selfId)
  // A different signer means a different password: never carry one across.
  const chooseSigner = (id: string) => {
    setSignAsId(id === selfId ? null : id)
    setSignPassword('')
    setSignError(null)
  }
  const signerPicker = (
    <SignerPicker
      label='Sign as'
      choices={signerChoiceList}
      value={chosenSignerId}
      onChange={chooseSigner}
      loading={signerRoster.isLoading}
      error={
        signerRoster.isError
          ? 'Could not load the list of signers.'
          : chosenSignerId !== null
            ? signerBlockedReason
            : null
      }
      disabled={verifying}
    />
  )
  const undoStart = useUndoStart()

  /** The step's expected start as an ISO instant, or null while it is unset. */
  const expectedStartIso = localToIso(startedAt) ?? null

  /**
   * When the FIRST unit is actually wanted.
   *
   * Prep is per requirement now, so there is no single offset to quote — the
   * confirm screen states the earliest moment anything gets held instead.
   */
  /**
   * The window start one row will be booked for — THE one place that answers it
   * for this component.
   *
   * A hand-picked override is the window start as given, with prep zeroed; the
   * default is the step's start plus that row's prep. Every caller here goes
   * through this so the Start record cannot say a different time than the row
   * was showing when it was signed. `ConfiguredProcessTabs.derivedWindow` is the
   * same rule for the rows themselves.
   */
  const rowWindowStart = (r: ProcessStepConnection['equipmentRequirements'][number]) => {
    const picked = localToIso(startOverrides[r.id] ?? '')
    if (picked) return reservationWindow(picked, { ...r, prepHrs: 0 })?.from
    return reservationWindow(expectedStartIso ?? '', r)?.from
  }

  const equipmentHeldFromIso = useMemo(() => {
    const starts = connection.equipmentRequirements
      .map(r => {
        // A LIVE HOLD FIRST. Its `startsAt` is what the calendar actually
        // booked; the derived value is only what Reserve would ask for, and it
        // is null whenever no expected start is set — which is exactly the case
        // where the operator most needs to be told their machines are held.
        const held = (reservations ?? []).find(res => res.requirementId === r.id)
        return held?.startsAt ?? rowWindowStart(r)
      })
      .filter((from): from is string => !!from)
    // Same-format UTC strings, so lexical order is chronological order.
    return starts.length > 0 ? starts.reduce((min, f) => (f < min ? f : min)) : null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection.equipmentRequirements, expectedStartIso, startOverrides, reservations])

  /**
   * What each requirement's expected start was RECORDED as when the step ran.
   *
   * Executions started before the single-expected-start change have a per-row
   * time in their selection JSON, and end mode must round-trip it untouched
   * rather than recompute it — see `persistSelections`.
   */
  const recordedExpectedStart = useMemo(
    () =>
      new Map(
        (execution?.equipmentSelections ?? [])
          .filter(s => !!s.expectedStart)
          .map(s => [s.requirementId, s.expectedStart as string]),
      ),
    [execution?.equipmentSelections],
  )

  // One window per requirement, not one spanning them all: they share a start
  // now, but each still has its own configured duration, so a shared window
  // would report a unit busy for requirement B because it is booked during A's
  // slot — steering operators away from valid choices.
  //
  // Built through the SAME module the booking path uses, so this preview cannot
  // ask about a different window than the one Start will request.
  const availabilityWindows = useMemo(() => {
    if (mode === 'complete') return []
    return connection.equipmentRequirements.flatMap(r => {
      // Through the same override-aware rule the rows and the booking use, so
      // the probe can never ask about a window other than the one Reserve will
      // request. An overridden row is askable even with no shared start set.
      const from = rowWindowStart(r)
      if (!from) return []
      const win = reservationWindow(from, { ...r, prepHrs: 0 })
      return win ? [{ key: r.id, ...win }] : []
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, connection.equipmentRequirements, expectedStartIso, startOverrides])

  const { data: availabilityByRequirement } = useAvailabilityForWindows(availabilityWindows)

  // ─── FEFO proposal ─────────────────────────────────────────────────────────
  //
  // Once the operator says WHEN, the system offers what it would pick: the stock
  // that expires first. Fetched only in start mode and only after a start is
  // set, because a proposal is a suggestion about a run that has not been
  // planned yet.
  const { data: inventoryProposal } = useInventoryProposal(
    submissionId,
    processFieldId,
    mode === 'start' && !!expectedStartIso,
  )

  // Requirements the operator has picked or unpicked by hand. A proposal never
  // overwrites one of these — that is what keeps this a suggestion. Deliberately
  // NOT cleared when the proposal refetches: a deliberate choice outlives a
  // background refresh.
  const [touchedLots, setTouchedLots] = useState<Set<string>>(new Set())
  const noteLotTouched = (requirementId: string) =>
    setTouchedLots(prev => (prev.has(requirementId) ? prev : new Set(prev).add(requirementId)))

  // Fill in only what the operator has not decided for themselves, and only
  // where they have decided NOTHING — a half-filled requirement is a decision in
  // progress, and completing it under them is exactly the "silent allocation"
  // MaterialLotAllocator's header warns about.
  useEffect(() => {
    if (mode !== 'start' || !inventoryProposal) return
    setLotAllocations(prev => {
      let changed = false
      const next = { ...prev }
      for (const p of inventoryProposal) {
        if (touchedLots.has(p.requirementId)) continue
        const already = Object.values(prev[p.requirementId] ?? {}).some(v => Number(v) > 0)
        if (already || p.lots.length === 0) continue
        next[p.requirementId] = Object.fromEntries(p.lots.map(l => [l.lot, String(l.qty)]))
        changed = true
      }
      return changed ? next : prev
    })
  }, [mode, inventoryProposal, touchedLots])

  /** Requirements whose proposal could not cover the run, keyed for the UI. */
  const shortByRequirement = useMemo(
    () => new Map((inventoryProposal ?? []).filter(p => p.isShort).map(p => [p.requirementId, p])),
    [inventoryProposal],
  )

  const handleReserveEquipment = async (items: ReserveEquipmentItem[]) => {
    setReserveError(null)
    setScanRefusal(null)
    try {
      await reserveEquipment.mutateAsync({ submissionId, fieldId: processFieldId, items })
    } catch (err) {
      // The type gate applies to the DROPDOWN too, not only to scans, so a
      // mismatch can arrive here. Render it as expected-versus-scanned in the
      // scan banner rather than flattening it to "could not reserve" — the
      // operator needs to know which machine the step actually wants. Checked
      // before the conflict narrowing, which would swallow the 409 reasons.
      if (isScanRefusal(err)) setScanRefusal(err.body)
      else if (isReservationConflict(err)) setReserveError({ kind: 'conflict', body: err.body })
      else
        setReserveError({
          kind: 'generic',
          message: apiErrorMessage(err, 'Could not reserve the equipment.'),
        })
    }
  }

  /**
   * Reserve inventory (z8nrz7dyfp follow-up): one call per requirement, run
   * together. Requirements succeed or fail independently — the batch WITHIN
   * one requirement is atomic (the backend's own transaction), but a shortage
   * on one material must not roll back a clean reserve on another.
   * `reservedByReq` only grows for the lines that actually landed, so a
   * partial failure leaves the button correctly still offering the rest.
   */
  const handleReserveInventory = async (
    items: { requirementId: string; lines: ReserveLine[] }[],
  ) => {
    setReserveInventoryError(null)
    const results = await Promise.allSettled(
      items.map(item =>
        reserveInventory.mutateAsync({
          submissionId,
          fieldId: processFieldId,
          requirementId: item.requirementId,
          payload: { lines: item.lines },
        }).then(() => item),
      ),
    )
    // Through `setHeld` so the ref and the rendered state move together: the
    // ref is what the typed-down release measures against, and a reserve that
    // only reached state would leave it releasing against a stale figure.
    // Assigned, not accumulated — `qty` is the total the step now holds on that
    // lot, so adding would double-count the moment Reserve is pressed twice.
    for (const r of results) {
      if (r.status !== 'fulfilled') continue
      const { requirementId, lines } = r.value
      for (const { lot, qty } of lines) setHeld(requirementId, lot, qty)
    }
    const failed = results.find(r => r.status === 'rejected') as PromiseRejectedResult | undefined
    if (failed) {
      setReserveInventoryError(apiErrorMessage(failed.reason, 'Could not reserve one or more lots.'))
    }
  }

  /**
   * Scan a tag: the server verifies the unit and holds it, and the row updates
   * to show what was booked.
   *
   * Returns whether the unit was held, which is what tells the field whether to
   * clear itself. See the inline notes for exactly which start time is sent —
   * it depends on the mode and on whether the target row is unambiguous.
   */
  const handleScanEquipment = async (
    requirementId: string,
    scannedCode: string,
  ): Promise<boolean> => {
    setReserveError(null)
    setScanRefusal(null)
    setScanningRequirementId(requirementId)
    // In START mode the step's expected start is a PLAN worth booking against.
    // In END mode there is none to preserve: `startedAt` is seeded from the
    // run's `plannedStart`, so sending it would back-date the hold to this
    // morning, and an elapsed window can collide with whatever really used the
    // machine then — refusing a scan for a unit sitting free in front of the
    // operator. End mode records use, so it stamps now.
    const plannedStart = mode === 'start' ? (expectedStartIso ?? '') : ''
    try {
      const result = await scanEquipment.mutateAsync({
        submissionId,
        fieldId: processFieldId,
        input: {
          scannedCode,
          requirementId,
          ...(plannedStart ? { startsAt: plannedStart } : {}),
        },
      })
      // Mirror the booking into the row, so the dropdown and the start field
      // show what was scanned rather than staying blank until the reservations
      // query settles. Keyed off the SERVER's `result.requirementId` rather than
      // the one sent: they agree now that the target binds, and trusting the
      // response keeps this correct if that ever changes again.
      const nextSelections = { ...selections, [result.requirementId]: result.equipmentId }
      // The scanned row's window is no longer mirrored into local state: the row
      // renders `held.startsAt` straight off the hold, which IS the truth of what
      // was booked, and there is no per-row plan left to preserve.
      //
      // Derived outside the updaters: StrictMode double-invokes them, which
      // would fire the save twice. Same reason as `onSelectEquipment`.
      setSelections(nextSelections)
      // A scan is a recorded selection like any other, so it must reach the
      // server the same way. In END mode nothing else on that screen persists,
      // and the scan flips the completeness gate — so without this an operator
      // could satisfy "select equipment before ending", sign Stop, and leave a
      // completed regulated run whose record names no equipment at all.
      if (mode === 'end') persistSelections(nextSelections, lotAllocations)
      else if (mode === 'start')
        onStartDraftChange({
          selections: nextSelections,
          lotAllocations,
          expectedStart: startedAt,
        })
      setLastScanVerified({
        requirementId: result.requirementId,
        label: result.equipmentLabel,
      })
      return true
    } catch (err) {
      setLastScanVerified(null)
      // SPLIT ON WHETHER THERE IS AN EARLIEST FREE TIME, not on which
      // vocabulary the body speaks. A busy unit satisfies BOTH predicates now
      // that the 409 carries both, so the branch order decides the render.
      //
      // Busy → conflict banner: the machine is the RIGHT type, so
      // expected-versus-scanned says nothing, while `busyUntil` is the only
      // fact the operator can act on.
      // Out of service → scan banner: it carries conflict vocabulary but has NO
      // free time (`busyUntil: null`). Keying on `isReservationConflict` alone
      // sent it to the conflict banner, which rendered "Earliest free slot:
      // 1/1/1970" — worse than useless on a regulated screen.
      //
      // The gate refusals (type_mismatch, wrong_unit, unknown_code…) carry no
      // `conflictType` at all and fall through to the scan banner untouched.
      if (isReservationConflict(err) && err.body.busyUntil)
        setReserveError({ kind: 'conflict', body: err.body })
      else if (isScanRefusal(err)) setScanRefusal(err.body)
      else
        setReserveError({
          kind: 'generic',
          message: apiErrorMessage(err, 'Could not verify the scan.'),
        })
      return false
    } finally {
      // In `finally` so a refusal releases the row's field too — otherwise the
      // scanner stays locked out of the row it just failed on, which is exactly
      // the row the operator is about to re-scan.
      setScanningRequirementId(null)
    }
  }

  const handleReleaseReservation = (reservationId: string) => {
    setReserveError(null)
    setScanRefusal(null)
    // Releasing is what the scan's verdict was ABOUT, so the green line must not
    // outlive the hold — it would keep claiming a unit is reserved seconds after
    // the operator let it go.
    setLastScanVerified(null)
    // A failed release used to vanish: the card stayed put with no explanation,
    // so the operator retried into the same silence. Surface it in the same
    // banner reserve failures use.
    void releaseReservation.mutateAsync(reservationId).catch((err: unknown) =>
      setReserveError({
        kind: 'generic',
        message: apiErrorMessage(err, 'Could not release the reservation.'),
      }),
    )
  }

  // In end mode the operator is recording what the run actually used, so every
  // edit is saved immediately rather than only on submit — a long run should not
  // lose its record because the modal was closed. The server merges, so sending
  // both maps is safe. Not e-signed: this is data capture, not a transition.
  const persistSelections = (
    equipment: Record<string, string>,
    lots: Record<string, Record<string, string>>,
  ) => {
    void updateSelections
      .mutateAsync({
        submissionId,
        fieldId: processFieldId,
        payload: {
          // `expectedStart` must be sent on every save. The server rebuilds each
          // selection from what it is given, so omitting it wrote null over the
          // times recorded at Start.
          //
          // What was RECORDED at Start wins over anything recomputed here. This
          // runs in end mode, where the step already ran: re-deriving from the
          // step's start would quietly rewrite history if the prep configuration
          // had been edited since, and this record is the Part 11 one.
          equipmentSelections: connection.equipmentRequirements
            .filter(r => !!equipment[r.id])
            .map(r => ({
              requirementId: r.id,
              equipmentId: equipment[r.id],
              expectedStart:
                recordedExpectedStart.get(r.id) ??
                rowWindowStart(r),
            })),
          inventorySelections: connection.inventoryRequirements.map(r => ({
            requirementId: r.id,
            lots: Object.entries(lots[r.id] ?? {})
              .filter(([, v]) => Number(v) > 0)
              .map(([lot, v]) => ({ lot, qty: Number(v) })),
          })),
        },
      })
      .catch((e: unknown) =>
        setSignError(
          apiErrorMessage(e, 'Could not save your equipment and lot selections.'),
        ),
      )
  }

  // Which mode we have already seeded for, so a re-render never re-seeds and
  // clobbers in-progress input.
  const seededFor = useRef<TransitionMode | null>(null)

  useEffect(() => {
    if (!mode) {
      seededFor.current = null
      // Closing is the only point every reopen passes through: the branches
      // below are per-mode, so resetting in one of them leaves the panel
      // standing when the modal is reopened in another.
      setUndoing(false)
      setUndoReason('')
      return
    }
    if (seededFor.current === mode) return
    // End mode seeds FROM the execution, so wait for it rather than seeding
    // from nothing and marking the job done — the record would never load in.
    if (mode === 'end' && !execution) return
    seededFor.current = mode

    setSignPassword('')
    setSignError(null)
    setSigning(false)
    setVerifying(false)
    confirming.current = false
    setStartedAt('')
    // A fresh sitting: the operator has decided nothing yet, so the FEFO
    // proposal is free to fill everything in again.
    setTouchedLots(new Set())
    setReserveError(null)
    // Both verdicts are about ONE scan in ONE sitting. Left standing, a refusal
    // banner would still accuse the operator of the wrong machine after the
    // modal reopens, and the green line would claim a unit is held for a step
    // that has not booked anything.
    setScanRefusal(null)
    setLastScanVerified(null)
    // A fresh sitting has reserved nothing via the button yet either — matches
    // Start opening with nothing allocated.
    setReservedByReq({})
    heldRef.current = {}
    setReserveInventoryError(null)
    // Let the live holds seed this sitting's allocations once they arrive; see
    // the effect below.
    holdsSeeded.current = false
    // Start opens with NOTHING allocated. This used to auto-suggest the pick
    // list — the first lot of each material prefilled with the whole configured
    // quantity — but which lot leaves the shelf is the operator's call, made
    // against the physical stock in front of them: expiry, what is already open,
    // what the fridge actually holds. A prefilled lot is a decision the record
    // attributes to them, and a signed one, since Start places the hold and the
    // 21 CFR Part 11 trail says they picked it. Defaulting is how the wrong lot
    // gets consumed by an operator who trusted the form.
    //
    // The per-material badge reads "0 / N" in amber until they allocate, so the
    // outstanding work stays visible without a prefill standing in for it.
    if (mode === 'start') {
      // A LIVE HOLD IS A SELECTION, and the authoritative one.
      //
      // Holds live on the server; `startDraft` is local state that a reload
      // throws away. Seeding from the draft alone meant a step whose machines
      // were genuinely booked reopened claiming "no unit selected" for every
      // row — and worse, the Start payload filters on `selections`, so those
      // held units were dropped from the signed record entirely.
      //
      // Holds are spread LAST so they win: the dropdown is disabled while a row
      // is held, so a draft that disagrees is stale by definition.
      setSelections({
        ...(startDraft?.selections ?? {}),
        ...Object.fromEntries(
          (reservations ?? []).map(res => [res.requirementId, res.equipmentId]),
        ),
      })
      setLotAllocations(startDraft?.lotAllocations ?? {})
      // Carried in from the row where it may already have been set, else the
      // plan RECORDED on the step, else the PROCEDURE'S OWN SCHEDULE.
      //
      // A run scheduled from the procedures list has already stated when it
      // starts; opening one of its steps to an empty expected start asks the
      // operator to type a plan the run already has, and — because reserving is
      // gated on this field — makes a scheduled run look unbookable. The stored
      // plan sits between the two so a reload does not send an applied step
      // back to the run's date. `||` not `??`: an unset draft is an empty
      // string, not null.
      setStartedAt(
        startDraft?.expectedStart ||
          (execution?.plannedStart ? isoToLocalInput(execution.plannedStart) : '') ||
          (scheduledStart ? isoToLocalInput(scheduledStart) : ''),
      )
    } else if (mode === 'end') {
      // A step may have started with nothing recorded, so End is where the
      // operator fills in what was actually used. Seed from whatever is already
      // stored so re-opening the modal shows their earlier entries.
      setSelections(
        Object.fromEntries(
          (execution?.equipmentSelections ?? []).map(s => [s.requirementId, s.equipmentId]),
        ),
      )
      setLotAllocations(
        Object.fromEntries(
          (execution?.inventorySelections ?? []).map(s => [
            s.requirementId,
            Object.fromEntries(s.lots.map(l => [l.lot, String(l.qty)])),
          ]),
        ),
      )
      // The step-wide planned start, shown locked in the header. Each unit's own
      // recorded expected start is read straight off the execution by
      // `recordedExpectedStart` — there is no per-row input left to seed.
      setStartedAt(execution?.plannedStart ? isoToLocalInput(execution.plannedStart) : '')
    } else {
      setSelections({})
      setLotAllocations({})
    }
    // Deliberately keyed on `mode` alone. `execution`, `connection` and
    // `lotsFor` are query/callback identities that change on refetch — and in
    // end mode picking a unit persists, invalidating `execution`, which re-ran
    // this and wiped every other requirement's typed date. Seeding is a
    // once-per-open concern, not a subscription.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, execution])

  /**
   * A LIVE INVENTORY HOLD IS A SELECTION — the same rule the block above states
   * for equipment, which inventory never got.
   *
   * `startDraft` is component state in the parent: closing the modal keeps it,
   * reloading the page does not. So a step that had genuinely reserved stock
   * reopened with the lot unticked and nothing allocated, while the server went
   * on holding it — the operator's work looked lost, they reserved again, and
   * the hold they could no longer see was invisible until Stop settled it.
   *
   * Seeded once per sitting rather than in the block above, because the holds
   * arrive from their own query and are usually not back yet when that runs.
   * The hold wins over the draft, but never over a lot whose hold the operator
   * has already changed this sitting (`heldRef`), so a typed-down quantity is
   * never overwritten by the hold it is releasing.
   */
  useEffect(() => {
    // Fresh holds only: coming back from the pick list, the cache still has the
    // holds from before the pick, and seeding from those showed the old figures
    // until a reload (z8nrz7fn5r).
    if (mode !== 'start' || holdsSeeded.current || !holdsFresh) return
    holdsSeeded.current = true
    const held = Object.entries(serverHolds).filter(([, byLot]) => Object.keys(byLot).length > 0)
    if (held.length === 0) return
    setLotAllocations(prev => {
      const next = { ...prev }
      for (const [requirementId, byLot] of held) {
        const row = { ...(next[requirementId] ?? {}) }
        // The hold wins over the draft, as it does for equipment: a pick list
        // changes the hold, not this page's draft, so a draft that disagrees
        // is the stale one. A drafted lot with no hold is left as it is.
        for (const [lot, qty] of Object.entries(byLot)) {
          if (heldRef.current[requirementId]?.[lot] === undefined) row[lot] = String(qty)
        }
        next[requirementId] = row
      }
      return next
    })
  }, [mode, serverHolds, holdsFresh])

  if (!mode) return null
  const action = mode === 'start' ? 'Start' : mode === 'end' ? 'Stop' : 'Complete'
  // Start and End both let the operator record equipment/lots; Complete is the
  // closing review and must not have the record edited under it.
  const editable = mode !== 'complete'
  const fromTo =
    mode === 'start'
      ? 'Assigned → Started · actual start written to audit trail'
      : mode === 'end'
        ? 'Started → Pending · actual stop time written to audit trail'
        : 'Pending → Completed · completion written to audit trail'

  // ENDING requires every configured equipment type to have a unit assigned…
  // Starting deliberately requires none of this — a run starts with an assignee
  // and nothing else, and the operator records what it used as they go.
  const equipmentComplete =
    mode !== 'end' || connection.equipmentRequirements.every(r => !!selections[r.id])
  // …and every configured material to have something recorded against it.
  //
  // Drawing LESS than configured is a legitimate record — the step configures
  // what it expects to draw, the record states what was actually drawn, and an
  // operator who opened one bag of a configured five returns the other four and
  // ends having used one. So the gate is "something is recorded for every
  // material, and none of it exceeds what the step configures", matching the
  // server's own rule in `resolveInventorySelections`. Requiring an exact match
  // made the record overstate consumption, which is the opposite of the point.
  const inventoryComplete =
    mode !== 'end' ||
    connection.inventoryRequirements.every(r => {
      const allocated = allocatedQty(lotAllocations[r.id])
      return allocated > 1e-6 && allocated <= r.qtyRequired + 1e-6
    })
  // A password is either supplied or it is not — whether it is the RIGHT one is
  // the server's answer, not something to guess at here. That is the difference
  // from the typed-name rule this replaced, which could be checked client-side
  // precisely because it proved nothing.
  const passwordEntered = signPassword !== ''
  const canSign =
    signer !== '' && passwordEntered && equipmentComplete && inventoryComplete && !signerBlockedReason
  /**
   * Why the RECORD is not yet signable — the gate on phase one.
   *
   * Kept apart from the password check on purpose: "the record is incomplete"
   * is a reason to stay on the working screen, while "you have not entered your
   * password" only means anything once the signature panel is open. Rolling
   * them into one string put "sign to continue" under a button whose signature
   * field had not been shown yet.
   */
  const recordBlockedReason = !equipmentComplete
    ? 'Select equipment for each configured type before ending'
    : !inventoryComplete
      ? 'Record what was drawn from each material — at least one lot, and no more than the step configures'
      : null

  // What signing will put on the record, built from what is ON SCREEN rather
  // than the stored execution: at the moment of confirming, the operator's
  // unsaved edits ARE what they are attesting to, and summarising the server's
  // older copy would show them something other than what they are signing.
  const summary = reservationSummary({
    mode,
    connection,
    selections,
    lotAllocations,
    equipmentTypeName,
    equipmentUnitName,
  })

  // The signature is the operator's own password, verified by the server
  // (see lib/signingToken.ts). A wrong one blocks the action.
  // The step has ONE expected start, and every equipment window is derived from
  // it, so setting it is now just setting it — no fanning the value out across
  // rows, and no per-row "did the operator touch this?" bookkeeping to respect.
  // Rows already reserved are unaffected either way: a live hold owns its window
  // until it is released.
  const applySharedStart = (value: string) => {
    setStartedAt(value)
    // In End mode the record is saved as it is edited, so the date has to be
    // written through rather than waiting for a submit that never carries it.
    if (mode === 'end') persistSelections(selections, lotAllocations)
    // In Start mode it is still unsigned input, so it goes back to the card —
    // which also owns the copy shown on the row.
    else if (mode === 'start')
      onStartDraftChange({ selections, lotAllocations, expectedStart: value })
  }

  // Reserved requirements have a fixed window, so once every one is held the
  // shared date no longer describes anything the operator can change by typing.
  // It locks rather than implying it still applies — and Edit below is the way
  // to change it, which moves the holds instead of leaving them behind.
  const allRequirementsReserved =
    connection.equipmentRequirements.length > 0 &&
    connection.equipmentRequirements.every(r =>
      (reservations ?? []).some(res => res.requirementId === r.id),
    )

  /**
   * What the booking automation has actually achieved, in the modal's own terms.
   *
   * Counting HELD REQUIREMENTS rather than reservation rows: a released hold is
   * kept in the table, so `reservations.length` would keep climbing as the
   * operator changed their mind and the chip would claim more units than the
   * step has.
   */
  const heldRequirements = connection.equipmentRequirements.filter(r =>
    (reservations ?? []).some(res => res.requirementId === r.id),
  ).length
  const automationBusy = reserveEquipment.isPending || retimeReservations.isPending

  /**
   * The three counts the panel reports, all derived — nothing new is stored.
   *
   * A unit is ASSIGNED once it has been picked (or is held, which implies it),
   * RESERVED once a live hold exists, VERIFIED once someone has scanned it at
   * the bench. Each is a superset of the next, so they only ever descend.
   */
  const assignedCount = connection.equipmentRequirements.filter(
    r => !!selections[r.id] || (reservations ?? []).some(res => res.requirementId === r.id),
  ).length
  const verifiedCount = connection.equipmentRequirements.filter(r =>
    (reservations ?? []).some(res => res.requirementId === r.id && !!res.actualStart),
  ).length

  /**
   * Commit an edited expected start.
   *
   * The server moves every hold atomically or refuses, so this either updates
   * the field AND the bookings together, or leaves both exactly as they were.
   * The local value is set only AFTER the call succeeds — setting it first would
   * show a time the step is not actually booked for whenever a unit is busy.
   */
  /**
   * Set or clear one row's hand-picked start.
   *
   * A blank value RESETS rather than storing an empty string, so clearing the
   * picker puts the row back on the derived time instead of leaving it with no
   * time at all — which would silently drop it out of `readyToReserve`.
   */
  const overrideRowStart = (requirementId: string, value: string) => {
    setRowRetimeError(null)
    setStartOverrides(prev => {
      if (!value) {
        const { [requirementId]: _cleared, ...rest } = prev
        return rest
      }
      return { ...prev, [requirementId]: value }
    })
  }

  /**
   * Move ONE already-held unit to a picked time.
   *
   * Unreserved rows need no round trip — their override is applied when Reserve
   * runs. A held row has a booking that must move with it, and the server does
   * that atomically: busy unit refuses, and the picker snaps back rather than
   * showing a time the unit is not actually held for.
   */
  const retimeRow = async (requirementId: string, value: string) => {
    const iso = localToIso(value)
    if (!iso || retimingRow) return
    setRowRetimeError(null)
    setRetimingRow(requirementId)
    try {
      await retimeReservations.mutateAsync({
        submissionId,
        fieldId: processFieldId,
        startsAt: iso,
        requirementId,
      })
      overrideRowStart(requirementId, value)
    } catch (err) {
      const body = (err as { body?: { message?: string; busyUntil?: string } })?.body
      setRowRetimeError({
        id: requirementId,
        message: body?.message
          ? body.busyUntil
            ? `${body.message} (free from ${formatStamp(body.busyUntil)})`
            : body.message
          : err instanceof Error
            ? err.message
            : 'Could not move this hold.',
      })
    } finally {
      setRetimingRow(null)
    }
  }

  /**
   * What re-timing to the drafted start would do, computed for the dialog.
   *
   * Through the shared preview module, which derives its times from the same
   * `reservationWindow` the booking uses — so the table cannot promise a time
   * the re-time would not produce.
   */
  const retimeDraftIso = localToIso(retimeDraft)
  const retimeOutcome = retimePreview({
    requirements: connection.equipmentRequirements,
    reservations: reservations ?? [],
    newStartIso: retimeDraftIso ?? '',
    labelFor: (equipmentId, equipmentTypeId) =>
      equipmentUnitName(equipmentId) || equipmentTypeName(equipmentTypeId),
  })

  const commitRetime = async () => {
    const iso = localToIso(retimeDraft)
    if (!iso || retimeReservations.isPending) return
    setRetimeError(null)
    try {
      await retimeReservations.mutateAsync({
        submissionId,
        fieldId: processFieldId,
        startsAt: iso,
      })
      applySharedStart(retimeDraft)
      setRetiming(false)
      setRetimeAsking(false)
    } catch (err) {
      const body = (err as { body?: { message?: string; busyUntil?: string } })?.body
      setRetimeError(
        body?.message
          ? body.busyUntil
            ? `${body.message} (free from ${formatStamp(body.busyUntil)})`
            : body.message
          : err instanceof Error
            ? err.message
            : 'Could not move the reservations.',
      )
    }
  }

  const handleConfirm = async () => {
    if (!canSign || confirming.current) return
    confirming.current = true
    setSignError(null)
    setVerifying(true)
    try {
      // Minted BEFORE the transition and spent by the server as it applies it,
      // so a wrong password cannot get as far as a half-written step.
      const signingToken = await mintSigningTokenFor(signPassword, signAsId, selfId)
      const payload = {
        signatureMeaning: signingMeaning(mode, connection.isKitStep),
        signingToken,
        ...(mode === 'start'
          ? {
              // For Sign as the server records the signer's own name here
              // whatever is sent; this is what it shows until then.
              performedBy: chosenName || undefined,
              // The planned start, not the actual one: this field is the shared
              // expected start for the step's equipment. `actualStart` is left
              // off deliberately so the server stamps the real moment the run
              // began — a typed time is a plan, and the audit trail should not
              // record it as the observed one.
              plannedStart: localToIso(startedAt),
              // Start no longer requires equipment or lots, so only send the
              // ones actually chosen. Sending an entry with no equipmentId
              // fails EquipmentSelectionDto's @IsString/@MinLength and the
              // whole request 400s — which is what "start with nothing filled
              // in" would otherwise hit every time.
              equipmentSelections: connection.equipmentRequirements
                .filter(r => !!selections[r.id])
                .map(r => ({
                  requirementId: r.id,
                  equipmentId: selections[r.id],
                  // Derived, not typed: the window this unit is wanted for is
                  // the step's start plus prep. Recorded per selection so the
                  // run's own record still says when each unit was due, even
                  // after the step's staffing configuration is edited later.
                  expectedStart: rowWindowStart(r),
                })),
              inventorySelections: connection.inventoryRequirements
                .map(r => ({
                  requirementId: r.id,
                  lots: Object.entries(lotAllocations[r.id] ?? {})
                    .filter(([, v]) => Number(v) > 0)
                    .map(([lot, v]) => ({ lot, qty: Number(v) })),
                }))
                .filter(s => s.lots.length > 0),
            }
          : {}),
      }
      const result = await transition.mutateAsync({
        action: mode,
        submissionId,
        fieldId: processFieldId,
        payload,
      })
      if (mode === 'start') {
        // The step IS running — a unit that could not be held does not undo
        // that, and the server deliberately does not fail the start over a busy
        // machine. But it must not pass in silence either: without this the
        // operator would believe the calendar holds a machine it does not.
        setReserveError(
          result?.reservationRefusals?.length
            ? {
                kind: 'generic',
                message: `Started, but ${result.reservationRefusals.length} unit${
                  result.reservationRefusals.length === 1 ? ' was' : 's were'
                } not reserved: ${result.reservationRefusals
                  .map(r => r.message)
                  .join('; ')}. Reserve manually or pick another unit.`,
              }
            : null,
        )
        // Starting is the beginning of the operator's work, not the end of it:
        // the run is now under way and recording what it uses is the job.
        // Closing here sent them back to the card to press Continue and reopen
        // the very screen they were on — and, now that Start no longer
        // pre-fills lots, to reopen it with the allocation still to do.
        //
        // Hand over to `end`, which IS that working screen. Marked as already
        // seeded so the switch keeps what was just entered: end-mode seeding
        // reads from `execution`, which is still the pre-start row until the
        // refetch lands, and would blank the form under them.
        seededFor.current = 'end'
        // The next transition is signed in its own right, and the operator is
        // handed back to the working screen rather than to another signature
        // panel — `seededFor` above means the seeding effect will not do it.
        setSignPassword('')
        setSignError(null)
        setSigning(false)
        // Both scan verdicts belong to the phase just signed off. `seededFor`
        // above makes the seeding effect early-return, so its resets never run
        // on this path — leaving "Verified and reserved AP-1" asserted across
        // the whole run, or a refusal masking a later genuine verification.
        setScanRefusal(null)
        setLastScanVerified(null)
        onTransitioned()
        onStarted()
        return
      }
      onTransitioned()
      onClose()
    } catch (err) {
      // Surface what the server actually said. A generic "try again" is
      // actively misleading for a 403 or 400 — those never succeed on retry,
      // and the message is the only thing that tells the operator what to fix
      // (assign the step first, complete the lot allocations, and so on).
      setSignError(apiErrorMessage(err, `Could not ${mode} the process step. Please try again.`))
    } finally {
      confirming.current = false
      setVerifying(false)
    }
  }

  const handleUndo = async () => {
    if (undoReason.trim() === '' || !passwordEntered || signerBlockedReason || confirming.current) return
    confirming.current = true
    setSignError(null)
    setVerifying(true)
    try {
      const signingToken = await mintSigningTokenFor(signPassword, signAsId, selfId)
      await undoStart.mutateAsync({
        submissionId,
        fieldId: processFieldId,
        payload: {
          signatureMeaning: UNDO_SIGNING_MEANING,
          reason: undoReason.trim(),
          signingToken,
        },
      })
      onTransitioned()
      onClose()
    } catch (err) {
      // Same reasoning as handleConfirm: show what the server said. A 403 here
      // means someone else owns the step, and "try again" would never fix it.
      setSignError(apiErrorMessage(err, 'Could not void the start. Please try again.'))
    } finally {
      confirming.current = false
      setVerifying(false)
    }
  }

  return (
    <>
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className='max-h-[92vh] gap-0 overflow-y-auto p-0 sm:max-w-3xl'>
        {/* Header — two settled rows instead of one wrapping one.
            Row 1 is what this dialog IS and what it does; row 2 is the run's
            standing facts on the left and the one setting that governs the
            whole step on the right. Everything here used to run along a single
            row — action button, title, chip, a bare date input, a timestamp —
            so at narrow widths the primary action could end up beneath the
            metadata. Keeping the date up here rather than in the body leaves
            the body to the process itself. */}
        <div className='border-b border-gray-100 bg-gray-50/60 px-6 py-4'>
          <div className='flex flex-wrap items-start justify-between gap-3 pr-14'>
            <h2 className='min-w-0 truncate text-lg font-semibold tracking-tight text-gray-900'>
              {action} Process Step
            </h2>

            {/* Both actions sit together, and only on the working screen: the
                signature panel below carries its own Back / Sign pair, and a
                second live transition button up here would compete with it. */}
            {!signing && !undoing && (
              <div className='flex shrink-0 items-center gap-2'>
                {/* Only a run actually under way can have its start voided.
                    Outline weight — the rare escape hatch, not a peer of the
                    transition beside it. */}
                {mode === 'end' && execution?.status === 'STARTED' && (
                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => {
                      setSignPassword('')
                      setSignError(null)
                      setSignAsId(null)
                      setUndoing(true)
                    }}
                    className='h-10 border-red-200 px-4 font-medium text-red-600 hover:bg-red-50 hover:text-red-700'
                  >
                    <Undo2 className='mr-1.5 h-4 w-4' />
                    Undo start
                  </Button>
                )}
                {/* Phase one. This opens the signature panel; it does not
                    transition anything, so it is gated on the RECORD being
                    complete and not on a name that has not been asked for yet.
                    The reason it is grey is stated directly beneath it. */}
                <Button
                  type='button'
                  disabled={!!recordBlockedReason || verifying}
                  title={recordBlockedReason ?? undefined}
                  onClick={() => {
                    setSignError(null)
                    setSignAsId(null)
                    setSigning(true)
                  }}
                  className={cn(
                    'h-10 px-5 font-semibold text-white shadow-sm',
                    // Stopping a run is the destructive-feeling step of the
                    // three — green would read as "proceed" on an action that
                    // halts work. Starting uses theme primary so it tracks the
                    // app's palette; Complete stays green as the closing,
                    // everything-passed action.
                    mode === 'end'
                      ? 'bg-red-600 hover:bg-red-700'
                      : mode === 'complete'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-primary hover:bg-primary/90',
                  )}
                >
                  {mode === 'complete' ? (
                    <CheckCircle2 className='mr-1.5 h-4 w-4' />
                  ) : mode === 'end' ? (
                    <Square className='mr-1.5 h-4 w-4 fill-current' />
                  ) : (
                    <Play className='mr-1.5 h-4 w-4 fill-current' />
                  )}
                  {action}
                </Button>
              </div>
            )}
          </div>

          {/* THE STEP'S ONE TIME, AND WHAT FOLLOWS FROM IT — the setting, what
              it has produced, and when each unit is wanted, in one panel because
              they are one thought. The run's own timestamps sit beneath it. */}
          {editable && connection.equipmentRequirements.length > 0 && (
            <div className='mt-3 rounded-lg border border-gray-200 bg-white px-4 py-3'>
              <div className='flex flex-wrap items-start justify-between gap-x-4 gap-y-3'>
                <div className='flex flex-col items-start gap-1'>
                  <label
                    htmlFor='process-shared-start'
                    className='text-[10px] font-medium uppercase tracking-wide text-gray-500'
                  >
                    Step starts
                  </label>
                  <Input
                    id='process-shared-start'
                    type='datetime-local'
                    value={retiming ? retimeDraft : startedAt}
                    onChange={e =>
                      retiming ? setRetimeDraft(e.target.value) : applySharedStart(e.target.value)
                    }
                    // Locked while held, EXCEPT while re-timing — that is the
                    // whole point of the Edit button beside it.
                    disabled={allRequirementsReserved && !retiming}
                    aria-label='Expected start for all equipment'
                    title={
                      retiming
                        ? 'Pick a new time, then Apply to move every hold to it'
                        : allRequirementsReserved
                          ? 'Every unit is reserved — Edit to move them all to a new time'
                          : 'Sets the expected start for every equipment requirement below — each can still be changed individually'
                    }
                    // 240, not 220 and certainly not 200: the native
                    // datetime-local renders its own calendar button INSIDE the
                    // box, and its widest state is the EMPTY one — the
                    // placeholder "dd/mm/yyyy, --:-- --" is longer than any
                    // value it will hold. 220 was measured against a filled
                    // field and still clipped the button before a time was set.
                    // Matches the per-row picker above; keep the two in step.
                    className={cn(
                      'h-8 w-[240px] max-w-full shrink-0 text-xs',
                      allRequirementsReserved && !retiming
                        ? 'bg-gray-100 text-gray-600'
                        : 'bg-white',
                    )}
                  />

                  {allRequirementsReserved &&
                    (retiming ? (
                      <div className='flex shrink-0 items-center gap-1'>
                        <Button
                          type='button'
                          // ASKS, then moves. Applying straight from here meant
                          // the operator learned what the change did — or why it
                          // was refused — only after committing to it.
                          onClick={() => {
                            setRetimeError(null)
                            setRetimeAsking(true)
                          }}
                          disabled={!retimeDraftIso || retimeReservations.isPending}
                          className='h-8 px-3 text-xs font-semibold'
                        >
                          Apply
                        </Button>
                        <Button
                          type='button'
                          variant='ghost'
                          onClick={() => {
                            setRetiming(false)
                            setRetimeError(null)
                          }}
                          disabled={retimeReservations.isPending}
                          className='h-8 px-2 text-xs'
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type='button'
                        variant='outline'
                        onClick={() => {
                          // Seeded from the committed value, so opening Edit and
                          // cancelling cannot leave a half-typed time behind.
                          setRetimeDraft(startedAt)
                          setRetimeError(null)
                          setRetiming(true)
                        }}
                        className='h-8 shrink-0 px-3 text-xs font-semibold'
                      >
                        Edit
                      </Button>
                    ))}
                </div>

                {/* WHAT THE SETTING HAS PRODUCED. Assigned, reserved and
                    verified are three different questions — a unit can be
                    picked without being held, and held without anyone having
                    been to it — so they get three numbers, not one chip. */}
                <div className='flex flex-col items-end gap-1.5'>
                  <UnitCounters
                    assigned={assignedCount}
                    reserved={heldRequirements}
                    verified={verifiedCount}
                    total={connection.equipmentRequirements.length}
                  />
                  {automationBusy && (
                    <AutomationStatus
                      held={heldRequirements}
                      total={connection.equipmentRequirements.length}
                      busy
                      busyLabel={retimeReservations.isPending ? 'Moving holds…' : 'Reserving…'}
                      className='py-px'
                    />
                  )}
                </div>
              </div>

              {/* Refusals are shown INSIDE the confirm dialog now, beside the
                  change they refused, rather than as a line under a form the
                  operator has already looked away from. */}
            </div>
          )}

          {/* The run's own timestamps, once it has any. Below the plan, because
              that is the order they happen in. */}
          {execution?.actualStart && (
            <p className='mt-2 font-mono text-[11px] tabular-nums text-muted-foreground'>
              Started {formatStamp(execution.actualStart)}
              {execution.actualEnd ? ` → ended ${formatStamp(execution.actualEnd)}` : ''}
            </p>
          )}

          {/* Beside the button that is grey, not in the footer's far corner. */}
          {!signing && !undoing && recordBlockedReason && (
            <p className='mt-2 flex items-center gap-1.5 text-xs font-medium text-amber-700'>
              <AlertTriangle className='h-3.5 w-3.5 shrink-0' />
              {recordBlockedReason}
            </p>
          )}
        </div>

        {/* Body — the configured process, grouped into tabs */}
        <div className='space-y-3 px-6 py-5'>
          {reserveError && (
            <p className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700'>
              {reserveError.kind === 'conflict' ? (
                <>
                  {reserveError.body.message}
                  {/* Only when there IS one. An out-of-service unit sends
                      `busyUntil: null`, and `new Date(null)` is the epoch — an
                      unguarded render told the operator the machine frees up on
                      1 January 1970. Reachable from the dropdown path too, so
                      the guard belongs here rather than at the call sites. */}
                  {reserveError.body.busyUntil
                    ? ` Earliest free slot: ${formatStamp(reserveError.body.busyUntil)}.`
                    : null}
                </>
              ) : (
                reserveError.message
              )}
            </p>
          )}
          {reserveInventoryError && (
            <p className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700'>
              {reserveInventoryError}
            </p>
          )}
          <ConfiguredProcessTabs
            submissionId={submissionId}
            fieldId={processFieldId}
            mode={mode}
            connection={connection}
            selections={editable ? selections : undefined}
            onSelectEquipment={
              editable
                ? (requirementId, equipmentId) => {
                    // Derived outside the updater: React double-invokes updaters
                    // in StrictMode, which would send the save twice.
                    const next = { ...selections, [requirementId]: equipmentId }
                    setSelections(next)
                    if (mode === 'end') persistSelections(next, lotAllocations)
                    else if (mode === 'start')
                      onStartDraftChange({
                        selections: next,
                        lotAllocations,
                        expectedStart: startedAt,
                      })
                  }
                : undefined
            }
            assigned={mode === 'complete' ? execution?.equipmentSelections : undefined}
            lotAllocations={editable ? lotAllocations : undefined}
            onAllocateLot={
              editable
                ? (requirementId, lot, value, commit) => {
                    // From here on this requirement is the operator's, and the
                    // FEFO proposal stops touching it.
                    noteLotTouched(requirementId)
                    const next = {
                      ...lotAllocations,
                      [requirementId]: { ...lotAllocations[requirementId], [lot]: value },
                    }
                    setLotAllocations(next)
                    if (mode === 'end') persistSelections(selections, next)
                    else if (mode === 'start') {
                      onStartDraftChange({
                        selections,
                        lotAllocations: next,
                        expectedStart: startedAt,
                      })
                      // Untick (or a typed-down qty) below what the Reserve
                      // button already committed must release the difference —
                      // otherwise the server keeps a real hold the row no
                      // longer shows, silently. `returnInventory` is the same
                      // call the manual `[+]` control makes; there is no
                      // separate "unreserve" endpoint.
                      // The `[+]` return has already moved this stock. Record
                      // the figure it left behind and stop: reading this as a
                      // typed-down edit released the remainder a second time —
                      // returning 5 of 10 released the 10, re-held 5, then
                      // released that 5 too, so the step held nothing and a
                      // reload showed no reservation at all.
                      // Only a FINISHED value may release. Every keystroke
                      // reaches this handler, so releasing on each one returned
                      // stock the operator was only passing through: 10 edited
                      // to 15 goes via "1" and handed 9 units back, clearing the
                      // box first gave up the whole hold, and each wrote its own
                      // row to the audit trail. A tick, an untick, a blur or
                      // Enter is a decision; a half-typed number is not.
                      if (!commit) return
                      // Read from the REF, not from `reservedInventory`.
                      //
                      // That memo only recomputes on the next render, so two
                      // keystrokes inside one React batch both closed over the
                      // same pre-release figure and each sent the full
                      // difference — 10 down to 4 released 6 twice, and the
                      // second arrived as "Cannot return 6: only 4 is still
                      // held". The ref is written synchronously below, so the
                      // next keystroke measures against what is already on its
                      // way out no matter when React re-renders.
                      //
                      // Falls back to the merged figure the first time a lot is
                      // touched: after a reload the session map is empty and
                      // only the server knows what is held.
                      const alreadyReserved =
                        heldRef.current[requirementId]?.[lot] ??
                        reservedInventory[requirementId]?.[lot] ??
                        0
                      const newQty = Number(value) || 0
                      const consumableId = connection.inventoryRequirements.find(
                        r => r.id === requirementId,
                      )?.consumableId
                      const lotId = consumableId
                        ? lotsFor(consumableId).find(l => l.lot === lot)?.id
                        : undefined
                      if (alreadyReserved > newQty + 1e-9 && lotId) {
                        const giveBack = alreadyReserved - newQty
                        // Recorded BEFORE the request, and SYNCHRONOUSLY in the
                        // ref, so the very next keystroke — even one in the same
                        // React batch, before any re-render — subtracts from
                        // what is already on its way out. State is updated too,
                        // but only so the row renders; it is never what the next
                        // decision reads.
                        setHeld(requirementId, lot, newQty)
                        returnInventory.mutate(
                          {
                            submissionId,
                            fieldId: processFieldId,
                            requirementId,
                            payload: {
                              destination: 'return_to_inventory',
                              lines: [{ lotId, qty: giveBack }],
                            },
                          },
                          {
                            onError: err => {
                              // Nothing was released, so put the figure back or
                              // the row would claim a hold it no longer shows.
                              setHeld(requirementId, lot, alreadyReserved)
                              setReserveInventoryError(
                                apiErrorMessage(err, 'Could not release the reserved quantity.'),
                              )
                            },
                          },
                        )
                      }
                    }
                  }
                : undefined
            }
            /**
             * A `[+]` return, which the server has already settled.
             *
             * One call for every lot it touched, so the merge happens once:
             * writing them one at a time built each `next` from the same
             * render's `lotAllocations`, and the second lot's write discarded
             * the first — leaving that lot claiming more than it holds and the
             * draft missing it entirely. Releases nothing: the stock has moved.
             */
            onAllocateLotMany={
              editable
                ? (requirementId, updates) => {
                    noteLotTouched(requirementId)
                    const next = {
                      ...lotAllocations,
                      [requirementId]: { ...lotAllocations[requirementId], ...updates },
                    }
                    setLotAllocations(next)
                    if (mode === 'end') persistSelections(selections, next)
                    else if (mode === 'start')
                      onStartDraftChange({
                        selections,
                        lotAllocations: next,
                        expectedStart: startedAt,
                      })
                    for (const [lot, value] of Object.entries(updates)) {
                      setHeld(requirementId, lot, Number(value) || 0)
                    }
                  }
                : undefined
            }
            reservedInventory={editable ? reservedInventory : undefined}
            onReserveInventory={editable ? handleReserveInventory : undefined}
            assignedInventory={mode === 'complete' ? execution?.inventorySelections : undefined}
            shortProposals={editable ? shortByRequirement : undefined}
            expectedStartIso={editable ? expectedStartIso : undefined}
            startOverrides={editable ? startOverrides : undefined}
            onOverrideStart={editable ? overrideRowStart : undefined}
            onRetimeRow={editable ? retimeRow : undefined}
            retimingRow={retimingRow}
            rowRetimeError={rowRetimeError}
            onStartUnit={editable ? id => void startReservation.mutateAsync(id) : undefined}
            startingUnit={startReservation.isPending ? startReservation.variables : null}
            reservations={editable ? reservations : undefined}
            onReserve={editable ? handleReserveEquipment : undefined}
            onRelease={editable ? handleReleaseReservation : undefined}
            onScan={editable ? handleScanEquipment : undefined}
            scanPendingFor={scanEquipment.isPending ? scanningRequirementId : null}
            scanRefusal={scanRefusal}
            lastScanVerified={lastScanVerified}
            fallbackStart={execution?.actualStart ?? null}
            availabilityFor={
              editable && availabilityByRequirement
                ? (requirementId, equipmentId) =>
                    availabilityByRequirement[requirementId]?.find(
                      a => a.equipmentId === equipmentId,
                    )
                : undefined
            }
          />
        </div>

        {/* Footer — the way out, and where the step stands. Nothing about
            signing lives here any more: the signature and the claim it attests
            to are a dialog of their own, opened by the action in the header. */}
        <div className='border-t border-gray-100 bg-gray-50/60 px-6 py-4'>
          <div className='flex flex-wrap items-center gap-3'>
            <button
              type='button'
              onClick={onClose}
              className='rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50'
            >
              Cancel
            </button>
            <p
              className={cn(
                'text-[11px]',
                signError ? 'font-medium text-red-600' : 'text-gray-400',
              )}
            >
              {signError ?? `Status ${fromTo}`}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Asked before any hold moves, and a SIBLING for the same reason the
        signature dialog is — see the note below. */}
    <RetimeConfirmDialog
      open={retimeAsking}
      stepName={field.label}
      currentStart={startedAt ? formatStamp(localToIso(startedAt) ?? '') : null}
      newStart={retimeDraftIso ? formatStamp(retimeDraftIso) : ''}
      preview={retimeOutcome}
      busy={retimeReservations.isPending}
      error={retimeError}
      formatStamp={formatStamp}
      onConfirm={commitRetime}
      onCancel={() => {
        setRetimeAsking(false)
        setRetimeError(null)
      }}
    />

    {/* The signature, in a dialog of its own — a SIBLING of the step dialog,
        not a child of its content. Both portal to the body so the rendered
        result is the same, but keeping them un-nested in the React tree avoids
        the focus-trap fight that made an earlier attempt at this unusable.
        Closing it returns to the step dialog, which has stayed open and
        unchanged underneath. */}
    <ProcessSignDialog
      open={signing && !undoing}
      title={TRANSITION_HEADING[mode]}
      description={field.label}
      meaning={signingMeaning(mode, connection.isKitStep)}
      signerName={chosenName}
      signerPicker={signerPicker}
      signingForOther={signingForOther}
      password={signPassword}
      onPasswordChange={setSignPassword}
      confirmLabel={`Sign & ${action}`}
      confirmDisabled={!canSign}
      tone={mode === 'end' ? 'red' : mode === 'complete' ? 'green' : 'primary'}
      busy={verifying}
      error={signError}
      onConfirm={handleConfirm}
      onCancel={() => {
        setSigning(false)
        setSignAsId(null)
        setSignPassword('')
        setSignError(null)
      }}
    >
      <TransitionConfirmScreen
        mode={mode}
        summary={summary}
        kit={connection.isKitStep ? kit : null}
        stepName={field.label}
        expectedStart={expectedStartIso ? formatStamp(expectedStartIso) : null}
        equipmentHeldFrom={equipmentHeldFromIso ? formatStamp(equipmentHeldFromIso) : null}
        alreadyHeld={heldRequirements > 0}
      />
    </ProcessSignDialog>

    <ProcessSignDialog
      open={undoing}
      title='Undo this start?'
      meaning={UNDO_SIGNING_MEANING}
      signerName={chosenName}
      signerPicker={signerPicker}
      signingForOther={signingForOther}
      password={signPassword}
      onPasswordChange={setSignPassword}
      confirmLabel='Confirm undo'
      confirmDisabled={undoReason.trim() === '' || !passwordEntered || !!signerBlockedReason}
      tone='red'
      busy={verifying}
      error={signError}
      onConfirm={handleUndo}
      onCancel={() => {
        setUndoing(false)
        setUndoReason('')
        setSignAsId(null)
        setSignPassword('')
        setSignError(null)
      }}
    >
      <div>
        <p className='text-xs text-muted-foreground'>
          The step returns to Assigned and its reserved equipment and stock are released. The
          original start stays in the audit trail.
        </p>
        <textarea
          value={undoReason}
          onChange={e => setUndoReason(e.target.value)}
          placeholder='Why is this start being voided? (required)'
          rows={3}
          aria-label='Reason for voiding this start'
          className='mt-3 w-full rounded-md border border-gray-200 px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring'
        />
      </div>
    </ProcessSignDialog>
    </>
  )
}

// ─── The renderer row: Process Step | Action | Status ────────────────────────

export function ProcessStepField({
  field,
  submissionId,
  locked,
  preview,
  onTransitioned,
}: {
  field: FieldDefinition
  submissionId: string
  locked: boolean
  preview: boolean
  /**
   * A signed transition landed. The host must re-read the SUBMISSION, not just
   * the executions: completing a kit run's step closes its form server-side
   * (KitStepPolicyService.onCompleted), so without this the page keeps offering
   * "Complete Form" against a submission the API has already closed — and
   * answers "Cannot complete a form with status: completed".
   */
  onTransitioned?: () => void
}) {
  // Process config is keyed by the FIELD LIBRARY field id — a palette insert
  // copies the field into the form under a new id, keeping `libraryFieldId` as
  // provenance. Resolve through it so the form field finds the config authored
  // on the Process tab (executions share the same key).
  const processFieldId = field.libraryFieldId ?? field.id
  // Builder preview renders the shell without hitting the API.
  //
  // Scoped to the SUBMISSION: read as a run, the server returns inventory
  // quantities already multiplied by the owning procedure's batch size, which
  // is what Start and End enforce. Reading it unscoped here would show an
  // operator a target that End then rejects.
  const { data: connection } = useProcessStepConnection(
    preview ? null : processFieldId,
    preview ? null : submissionId,
  )
  const { data: executionsData } = useProcessStepExecutions(preview ? null : submissionId)
  const executions = executionsData?.executions
  /**
   * Who the PROCEDURE says owns this form, for steps with no execution yet.
   *
   * Assigning a procedure cascades onto executions — but only ones that exist,
   * and a step nobody has claimed has none. This is the same answer, derived
   * from the procedure's step assignment, so the card is not blank on a step
   * that has an owner.
   */
  const inheritedAssignee = executionsData?.inheritedAssignee ?? null
  const { can } = usePermission()
  const { user, email: myEmail } = useAuth()
  // Declared here rather than beside the other derived flags below: it gates
  // the roster query, whose route 403s a caller without it.
  const canExecute = can(PERMISSIONS.processExecute)
  // Narrowed to the people qualified to work THIS step — whoever holds a
  // method covering it. A step no method covers still offers everyone, so this
  // is correct before the matrix is populated.
  const { users } = useProcessAssignableUsers(canExecute, processFieldId)
  const assign = useAssignExecution()
  const unassign = useUnassignExecution()
  const [modalMode, setModalMode] = useState<TransitionMode | null>(null)
  // Survives the modal unmounting; cleared once Start is signed and the
  // allocations become part of the record.
  const [startDraft, setStartDraft] = useState<StartDraft | null>(null)
  const [assignError, setAssignError] = useState<string | null>(null)

  // ─── Auto-select, offered off the row's expected-start field ───────────────
  //
  // Naming a time is the only input auto-select needs, so the offer follows the
  // field rather than hiding behind the Start dialog. Opened on COMMIT (blur or
  // Enter), never on keystroke — a `datetime-local` fires change on every
  // component of the value, and a dialog that appeared while someone was still
  // typing the year would be unusable.
  const [autoSelectOpen, setAutoSelectOpen] = useState(false)
  const [autoSelectError, setAutoSelectError] = useState<string | null>(null)
  const reserveForAutoSelect = useReserveEquipment()

  const execution: ProcessStepExecution | undefined = executions?.find(
    e => e.fieldId === processFieldId,
  )
  const status: ProcessStepExecutionStatus = execution?.status ?? 'ASSIGNED'
  // Only the materials already allocated on this step (z8nrz7e8kw).
  const { lotsFor } = useProcessMaterialLots((execution?.inventorySelections ?? []).map(sel => sel.consumableId))

  // A kit expires with its earliest component — derived here, never stored, so
  // it cannot contradict the lots it is derived from.
  const kitExpiry = useMemo(() => {
    if (!connection?.isKitStep) return null
    const dates = (execution?.inventorySelections ?? [])
      .flatMap(sel => {
        const lots = lotsFor(sel.consumableId)
        return sel.lots.map(l => lots.find(x => x.lot === l.lot)?.expiry)
      })
      .filter((d): d is string => !!d)
    return dates.length > 0 ? dates.reduce((min, d) => (d < min ? d : min)) : null
  }, [connection, execution, lotsFor])
  // ─── Automation state on the CARD ──────────────────────────────────────────
  //
  // Same query key the modal uses, so this is served from cache once the modal
  // has opened and costs one request otherwise. The card needs it to answer a
  // question the draft alone cannot: has this expected start already been acted
  // on? A plain editable field says "nothing has happened yet" on a step whose
  // machines are booked.
  // Skipped entirely for a step that configures no equipment: there is nothing
  // to hold, so the request would answer a question the card never asks — and a
  // form can carry many process fields, each of which would fire one.
  const cardEquipmentCount = connection?.equipmentRequirements.length ?? 0
  const { data: cardReservations } = useProcessStepReservations(
    cardEquipmentCount > 0 ? submissionId : null,
    cardEquipmentCount > 0 ? processFieldId : null,
  )

  const cardHeldCount = (connection?.equipmentRequirements ?? []).filter(r =>
    (cardReservations ?? []).some(res => res.requirementId === r.id),
  ).length
  /** The automation has been run and holds exist — so the date is now load-bearing. */
  const automationSet = cardHeldCount > 0

  /** Does applying a date actually assemble anything on this step? */
  const hasAutomatableWork =
    cardEquipmentCount > 0 || (connection?.inventoryRequirements.length ?? 0) > 0

  /** Persists the expected start. Declared here because both the apply below
      and `commitCardRetime` write through it. */
  const setPlannedStart = useSetPlannedStart()

  const cardRetime = useRetimeReservations()
  const [editingAutomation, setEditingAutomation] = useState(false)
  // The expected-start picker. Its own state rather than derived from
  // `editingAutomation`, which means something narrower — "this step is already
  // booked and the operator is re-timing it" — and is false for the ordinary
  // case of setting a date that was never applied.
  const [expectedStartOpen, setExpectedStartOpen] = useState(false)
  const [cardRetimeError, setCardRetimeError] = useState<string | null>(null)
  const [cardRetimeAsking, setCardRetimeAsking] = useState(false)

  /**
   * Change the automation's time from the card.
   *
   * Re-running auto-select here would be wrong: the units are already chosen and
   * held, and the operator is moving WHEN, not WHAT. So this re-times the
   * existing holds — atomically, so a busy machine leaves both the field and the
   * bookings as they were.
   */
  /** The drafted new start, held while the confirm dialog asks about it. */
  const [cardRetimeDraft, setCardRetimeDraft] = useState('')
  const cardRetimeDraftIso = localToIso(cardRetimeDraft) ?? null

  const commitCardRetime = async () => {
    const iso = cardRetimeDraftIso
    if (!iso || cardRetime.isPending) return
    setCardRetimeError(null)
    try {
      await cardRetime.mutateAsync({ submissionId, fieldId: processFieldId, startsAt: iso })
      // The holds moved, so the RECORDED plan has to move with them. Second,
      // and never first: a refused re-time must not leave the step claiming a
      // time its machines were never booked for.
      await setPlannedStart.mutateAsync({
        submissionId,
        fieldId: processFieldId,
        plannedStart: iso,
      })
      setStartDraft({
        selections: startDraft?.selections ?? {},
        lotAllocations: startDraft?.lotAllocations ?? {},
        expectedStart: cardRetimeDraft,
      })
      setEditingAutomation(false)
      setCardRetimeAsking(false)
    } catch (err) {
      const body = (err as { body?: { message?: string; busyUntil?: string } })?.body
      setCardRetimeError(
        body?.message
          ? body.busyUntil
            ? `${body.message} (free from ${formatStamp(body.busyUntil)})`
            : body.message
          : apiErrorMessage(err, 'Could not move the reservations.'),
      )
    }
  }

  // Assignment gates Start only — the server enforces the same rule, and End /
  // Complete are deliberately not assignee-restricted.
  // The EXECUTION wins when there is one: once the row exists it is the record
  // of who owns this step, including a deliberate handover away from whoever
  // the procedure named. The inherited value only fills the gap before that.
  //
  // Declared up here rather than beside the other assignment derivations
  // because `automationBlockedReason` below gates the expected-start controls,
  // which are read during render — a later `const` would be in its own
  // temporal dead zone by then.
  const assigneeId = execution?.assigneeId ?? inheritedAssignee?.id ?? null

  /**
   * The automation needs an owner before it runs.
   *
   * Applying the expected start here is what triggers auto-select, and that
   * places real holds on shared machines. Doing it on an unassigned step books
   * equipment for a run nobody is accountable for — and Start will refuse that
   * step anyway (`startBlockedReason`), so the holds would sit on the calendar
   * keeping other cases off a machine for work that cannot begin.
   *
   * Deliberately NOT gated on `assignedToMe`, unlike Start. Planning ahead for
   * someone else's run is ordinary scheduling work; running it without an owner
   * is the thing that has no meaning.
   */
  const automationBlockedReason =
    assigneeId == null ? 'Assign this step before setting an expected start' : null

  // ─── The step's expected start, as the CARD sees it ────────────────────────
  //
  // Three sources, most specific first:
  //
  //   1. the unapplied draft — what the operator is typing right now
  //   2. `execution.plannedStart` — the plan they have already applied
  //   3. the PROCEDURE'S scheduled date — what the run itself says
  //
  // (3) is why this exists. A run scheduled from the procedures list has
  // already stated when it starts, and the step card showed an empty field
  // anyway — so the operator retyped a date the run already held, and a
  // scheduled run read as unbookable because reserving is gated on this field.
  // The dialog had this fallback from the start; the card did not, which is
  // exactly backwards, since the card is where the date is normally set.
  //
  // (2) is what makes an applied plan survive a reload. It used to live only in
  // `startDraft`, so refreshing the page kept the equipment holds and lost the
  // time they were computed from — and the field then fell back to (3),
  // quietly contradicting the bookings.
  const [expectedStartError, setExpectedStartError] = useState<string | null>(null)
  /** The value the card is committed to — nothing to apply while the input matches it. */
  const storedExpectedStart =
    (execution?.plannedStart ? isoToLocalInput(execution.plannedStart) : '') ||
    (executionsData?.scheduledStart ? isoToLocalInput(executionsData.scheduledStart) : '')
  const expectedStartValue = startDraft?.expectedStart ?? storedExpectedStart
  const expectedStartDirty = expectedStartValue !== storedExpectedStart

  const setExpectedStartDraft = (value: string) => {
    setExpectedStartError(null)
    setStartDraft({
      selections: startDraft?.selections ?? {},
      lotAllocations: startDraft?.lotAllocations ?? {},
      expectedStart: value,
    })
  }

  /** Put the input back to the stored plan and drop any in-flight edit. */
  const cancelExpectedStartEdit = () => {
    setExpectedStartError(null)
    setCardRetimeError(null)
    setEditingAutomation(false)
    setStartDraft({
      selections: startDraft?.selections ?? {},
      lotAllocations: startDraft?.lotAllocations ?? {},
      expectedStart: storedExpectedStart,
    })
  }

  /**
   * Apply the date on screen. THE ONLY WAY THIS FIELD COMMITS.
   *
   * It used to commit on blur, and blur is not consent: this field books
   * machines and holds material, and the native `datetime-local` popup has no
   * notion of "done" — it closes when it feels like it, firing the automation
   * from an event the operator never consciously performed. The same reasoning
   * removed the blur-commit from the procedures list's own date cells.
   *
   * Two outcomes, by whether the automation has already run:
   *  - not yet — record the plan, then offer auto-select as it always did.
   *  - already booked — hand off to the re-time confirm, which MOVES the holds.
   */
  const applyExpectedStart = async () => {
    if (automationBlockedReason || setPlannedStart.isPending) return
    const iso = localToIso(expectedStartValue)
    if (!iso) return
    if (editingAutomation) {
      setCardRetimeDraft(expectedStartValue)
      setCardRetimeError(null)
      setCardRetimeAsking(true)
      // The confirm dialog takes over from here, and it must not open behind
      // the picker that launched it.
      setExpectedStartOpen(false)
      return
    }
    if (automationSet) return
    setExpectedStartError(null)
    try {
      // Persisted BEFORE auto-select opens, so a plan the operator applied is
      // recorded even if they then abandon the booking dialog — and so the
      // holds auto-select places can never outlive the time behind them.
      //
      // Skipped when the stored plan already says this: an audit row whose old
      // and new values match is noise in a Part 11 trail, and applying an
      // unchanged date is a legitimate way back to auto-select for a step whose
      // booking dialog was dismissed.
      if (expectedStartDirty || !execution?.plannedStart) {
        await setPlannedStart.mutateAsync({
          submissionId,
          fieldId: processFieldId,
          plannedStart: iso,
        })
      }
      // Applied, so the picker's work is done. Closed BEFORE the booking
      // dialog opens, so the two are never stacked.
      setExpectedStartOpen(false)
      // A step configuring neither equipment nor material has nothing to
      // assemble, and the booking dialog would open empty. Recording the plan
      // IS the whole action there.
      if (hasAutomatableWork) setAutoSelectOpen(true)
    } catch (err) {
      // Left OPEN on a refusal: the message renders under the row, and closing
      // the panel would throw away the date the operator just chose.
      setExpectedStartError(apiErrorMessage(err, 'Could not save the expected start.'))
    }
  }

  /**
   * Is there anything for the tick to do?
   *
   * Deliberately NOT "is it dirty" while the automation is unset. Two undirty
   * states still have work in them: a step seeded from the run's scheduled date
   * has never been applied at all, and a step whose plan is recorded but whose
   * auto-select was dismissed still has its equipment and material to pick.
   * Re-timing an existing booking is the one case where an unchanged value
   * means nothing to do.
   */
  const canApplyExpectedStart =
    !!localToIso(expectedStartValue) &&
    !automationBlockedReason &&
    (editingAutomation ? expectedStartDirty : true)

  // What auto-select would do, computed only while the dialog is open.
  const autoSelectStartIso = localToIso(expectedStartValue) ?? null
  const {
    data: autoSelectProposals,
    isFetching: autoSelectLoading,
    // READ, not ignored. The dialog used to key its Materials section off
    // `proposals.length`, so a failed proposal rendered exactly like a step
    // with no materials configured — the operator was shown equipment only and
    // told nothing was missing. The section is driven by the CONFIGURED
    // requirements now, and this says why it could not be filled in.
    isError: autoSelectProposalsFailed,
  } = useInventoryProposal(submissionId, processFieldId, autoSelectOpen)
  // Every requirement's window, asked about together so one round trip answers
  // "what is free at this time" for the whole step.
  const autoSelectWindows = useMemo(() => {
    if (!autoSelectOpen || !autoSelectStartIso) return []
    return (connection?.equipmentRequirements ?? []).flatMap(r => {
      const win = reservationWindow(autoSelectStartIso, r)
      if (!win) return []
      // Send the cap so the preview refuses what booking would refuse, and
      // exclude this requirement's OWN live hold — reserving releases it before
      // it counts the day, so leaving it in would make an already-booked step
      // preview every unit as used up.
      const own = (cardReservations ?? [])
        .filter(res => res.requirementId === r.id && res.releasedAt === null)
        .map(res => res.id)
      return [
        {
          key: r.id,
          ...win,
          runsPerDay: r.runsPerDay,
          ...(own.length > 0 ? { excludeReservationIds: own } : {}),
        },
      ]
    })
  }, [autoSelectOpen, connection?.equipmentRequirements, autoSelectStartIso, cardReservations])

  const { data: autoSelectAvailability, isFetching: autoSelectAvailLoading } =
    useAvailabilityForWindows(autoSelectWindows)
  // Every equipment unit and type in the tenant, for naming this step's own
  // requirements — so only for a step that has any (z8nrz7e8kw).
  const { data: autoSelectTypes } = useEquipmentTypes(cardEquipmentCount > 0)
  const { data: autoSelectUnits } = useEquipmentUnits(cardEquipmentCount > 0)

  // Same preview the modal shows, from the same module — the card and the
  // dialog must not describe one action two ways.
  //
  // BELOW the two queries above, and that is not stylistic: `const` bindings
  // are in the temporal dead zone until their own line runs, so computing this
  // earlier in the body threw "Cannot access 'autoSelectUnits' before
  // initialization" the moment the card rendered.
  const cardRetimeOutcome = retimePreview({
    requirements: connection?.equipmentRequirements ?? [],
    reservations: cardReservations ?? [],
    newStartIso: cardRetimeDraftIso ?? '',
    labelFor: (equipmentId, equipmentTypeId) =>
      (autoSelectUnits ?? []).find(u => u.id === equipmentId)?.label ??
      (autoSelectTypes ?? []).find(t => t.id === equipmentTypeId)?.name ??
      equipmentId,
  })

  /**
   * Which unit each equipment requirement gets.
   *
   * **First free unit of the right type, and nothing cleverer.** Ordering by
   * proximity to the operator is the stated next step, but neither `User` nor
   * the session carries a location today, so any "closest" ranking here would be
   * invented. Picking the first available one is honest about that and is what
   * the manual Reserve button already did.
   *
   * An operator's own choice always wins — auto-select fills gaps, it does not
   * overrule. A unit already taken by an earlier requirement in this same step
   * is skipped, so one machine is never booked against two rows of one run.
   */
  const autoSelectEquipment = useMemo(() => {
    if (!autoSelectStartIso) return []
    const typeNameOf = new Map((autoSelectTypes ?? []).map(t => [t.id, t.name]))
    const unitLabelOf = new Map((autoSelectUnits ?? []).map(u => [u.id, u.label]))
    const claimed = new Set<string>()

    return (connection?.equipmentRequirements ?? []).map(r => {
      const typeName = typeNameOf.get(r.equipmentTypeId) ?? r.equipmentTypeId
      const window = reservationWindow(autoSelectStartIso, r)
      if (!window) {
        return { requirementId: r.id, typeName, unitId: null, unitLabel: null, note: 'No run time configured' }
      }

      const chosen = startDraft?.selections?.[r.id]
      if (chosen) {
        claimed.add(chosen)
        return {
          requirementId: r.id,
          typeName,
          unitId: chosen,
          unitLabel: unitLabelOf.get(chosen) ?? chosen,
          note: 'Your choice',
        }
      }

      const slots = autoSelectAvailability?.[r.id] ?? []
      const free = slots.find(
        s => s.available && s.equipmentTypeId === r.equipmentTypeId && !claimed.has(s.equipmentId),
      )
      if (!free) {
        const busy = slots.find(s => s.equipmentTypeId === r.equipmentTypeId)
        return {
          requirementId: r.id,
          typeName,
          unitId: null,
          unitLabel: null,
          note: busy ? 'No unit free at this time' : 'No units of this type',
        }
      }
      claimed.add(free.equipmentId)
      return {
        requirementId: r.id,
        typeName,
        unitId: free.equipmentId,
        unitLabel: unitLabelOf.get(free.equipmentId) ?? free.equipmentId,
        note: null,
      }
    })
  }, [
    connection?.equipmentRequirements,
    startDraft?.selections,
    autoSelectStartIso,
    autoSelectAvailability,
    autoSelectTypes,
    autoSelectUnits,
  ])

  const autoSelectBookable = autoSelectEquipment.filter(e => !!e.unitId)

  const runAutoSelect = async () => {
    setAutoSelectError(null)
    if (!autoSelectStartIso) return

    // Both halves of the automation land here.
    //
    // INVENTORY: the proposed lots are written into the draft, so the Start
    // dialog opens with them already allocated. They are not HELD — holding
    // stock is part of the signed Start transition, and taking it without a
    // signature would be a regulated write with nobody's name on it.
    //
    // EQUIPMENT: the chosen units are written into the draft AND reserved,
    // because an equipment hold has never required a signature (the manual
    // Reserve button places one), and a unit nobody has claimed is exactly what
    // another run will take while this operator is still filling the form in.
    const nextSelections = { ...(startDraft?.selections ?? {}) }
    for (const e of autoSelectBookable) nextSelections[e.requirementId] = e.unitId!

    const nextLots = { ...(startDraft?.lotAllocations ?? {}) }
    for (const p of autoSelectProposals ?? []) {
      if (p.lots.length === 0) continue
      // An allocation the operator has already touched is theirs; auto-select
      // fills what is empty and leaves the rest alone.
      const existing = Object.values(nextLots[p.requirementId] ?? {}).some(v => Number(v) > 0)
      if (existing) continue
      nextLots[p.requirementId] = Object.fromEntries(p.lots.map(l => [l.lot, String(l.qty)]))
    }

    setStartDraft({
      selections: nextSelections,
      lotAllocations: nextLots,
      expectedStart: startDraft?.expectedStart ?? '',
    })

    if (autoSelectBookable.length === 0) {
      setAutoSelectOpen(false)
      return
    }
    try {
      await reserveForAutoSelect.mutateAsync({
        submissionId,
        fieldId: processFieldId,
        // The step start, unshifted: the server applies prep itself.
        items: autoSelectBookable.map(e => ({
          requirementId: e.requirementId,
          equipmentId: e.unitId!,
          startsAt: autoSelectStartIso,
        })),
      })
      setAutoSelectOpen(false)
    } catch (err) {
      // The draft keeps the picks — the operator can open Start and reserve or
      // change them by hand rather than redoing the selection.
      setAutoSelectError(apiErrorMessage(err, 'Could not reserve the equipment.'))
    }
  }

  const action = NEXT_ACTION[status]
  // A connection row alone is not a configured process. The builder creates an
  // empty one to anchor the field to its form, and ProcessPage already treats
  // "configured" as having at least one requirement — so treating a bare row as
  // configured here would offer Initiate on a step that does nothing.
  const configured =
    !!connection &&
    connection.inventoryRequirements.length +
      connection.equipmentRequirements.length +
      connection.staffingLines.length >
      0
  // Yourself first — claiming your own work is the common case, and it saves
  // hunting for your own name in a long roster.
  const orderedAssignees = useMemo(() => {
    const me = users.filter(u => u.id === user?.userId)
    return [...me, ...users.filter(u => u.id !== user?.userId)]
  }, [users, user?.userId])

  const assignedToMe = assigneeId != null && assigneeId === user?.userId
  const assignee = assigneeId ? users.find(u => u.id === assigneeId) : undefined
  // The roster holds enabled users only, so an assignee disabled after the fact
  // is missing from it — including yourself. Falling through to "another user"
  // for your own step would be plainly wrong, so self resolves to the signed-in
  // email instead. The inherited name is a further fallback: it is resolved
  // server-side, so it survives a roster this caller cannot see.
  const assigneeName = assigneeId
    ? (assignee?.name ??
      (assignedToMe ? (myEmail ?? 'me') : (inheritedAssignee?.name ?? 'another user')))
    : null
  // Shown at every status: assignment records who owns the step now, which can
  // change at any point — a handover mid-run, or a different reviewer picking
  // up the completion. It never rewrites who signed a transition; each of those
  // stamps its own actor server-side. `locked` still hides it, since the whole
  // submission is closed by then.
  //
  // Shown on kit runs too. Hiding it here was a mistake: this picker writes the
  // execution's assignee, which is the exact field start() checks, so removing
  // it left an unassigned kit with a permanently disabled Initiate and no way
  // to fix that from the step. The run-level "Assign To" now cascades onto this
  // same field, so the two controls agree rather than compete — which was the
  // real problem, not their number.
  const showAssignment = !preview && !locked && configured && canExecute
  const busy = assign.isPending || unassign.isPending

  // The picker's options. Labels carry the "(me)" suffix so the trigger — which
  // renders the selected option's label — reads exactly as the list does; on a
  // Part 11 record the person's NAME belongs there, since "You" forces a reader
  // to know whose session produced the screen.
  const assigneeOptions = useMemo(() => {
    const opts = orderedAssignees.map(u => ({
      value: u.id,
      label: u.id === user?.userId ? `${u.name} (me)` : u.name,
    }))
    // Clearing the owner is only offered before the run starts — the server
    // refuses it once under way, since a started step always has someone
    // accountable for it.
    if (assigneeId != null && status === 'ASSIGNED') {
      opts.unshift({ value: UNASSIGN_VALUE, label: 'Unassigned' })
    }
    // An assignee since disabled has left the roster, but the trigger still has
    // to name them: a step whose owner renders blank reads as unassigned.
    if (assigneeId != null && !opts.some(o => o.value === assigneeId)) {
      opts.push({
        value: assigneeId,
        label: assignedToMe ? `${assigneeName} (me)` : (assigneeName ?? 'another user'),
      })
    }
    return opts
  }, [orderedAssignees, user?.userId, assigneeId, status, assignedToMe, assigneeName])

  const startBlockedReason =
    action?.mode !== 'start'
      ? null
      : assigneeId == null
        ? 'Assign this step before starting it'
        : !assignedToMe
          ? `Only the assigned user can start this step${assigneeName ? ` — currently ${assigneeName}` : ''}`
          : null

  const disabled =
    preview || locked || !configured || !action || !canExecute || !!startBlockedReason

  const runAssignment = (fn: () => Promise<unknown>) => {
    setAssignError(null)
    void fn().catch((e: unknown) =>
      setAssignError(apiErrorMessage(e, 'Could not update the assignment.')),
    )
  }

  return (
    <div className='rounded-lg border-2 p-4' data-testid='process-step-card'>
      {/* The kit's identity and its live expiry. Derived from the lots already
          allocated rather than fetched: the value must track what is on screen,
          and a frozen figure would disagree the moment a lot is changed. The
          server freezes its own copy at End — this is the working view. */}
      {connection?.isKitStep && execution?.kitSupplyId && (
        <div className='mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-sky-200 bg-sky-50/70 px-3 py-2 text-xs'>
          <span className='font-semibold tracking-wide text-sky-900'>
            {execution.kitSupplyId}
          </span>
          <span className='text-sky-800'>
            Kit expiry:{' '}
            <span className='font-medium tabular-nums'>{kitExpiry ?? 'not yet allocated'}</span>
          </span>
          <span className='text-sky-700'>Earliest component expiry</span>
        </div>
      )}
      {/* Flow, not a fixed column count. This row carries a variable set of
          controls — the pick list, the assignee and the action each appear only
          under their own conditions — so a `grid-cols-[…]` with a hard-coded
          number of tracks mis-parents whichever field happens to be last and
          clips it. Wrapping on a baseline keeps every field the same height and
          lets the set grow without redesigning the row. */}
      <div className='flex flex-wrap items-end gap-x-4 gap-y-3'>
        <div className='min-w-[11rem] flex-1 space-y-1'>
          <p className='text-xs font-medium text-gray-500'>Process Step</p>
          <div className='flex h-9 items-center rounded-md border border-gray-200 bg-gray-50/70 px-3 text-sm font-medium text-gray-900'>
            {field.label}
          </div>
        </div>

        {/* Pick list — the operator assembling this step should not have to
            leave for a list page to print what they are about to fetch.
            Keyed on the step HAVING a bill of materials rather than on the form
            being a kit: a cleaning or MES step with materials needs its sheet
            just as much. Hidden, not disabled, when there is nothing to pick.

            A direct printer icon rather than the actions menu the list pages
            use: at the bench there is exactly one thing you want from this
            step, and a popover that only ever leads to Print is a wasted
            click. */}
        {!preview && configured && connection.inventoryRequirements.length > 0 && (
          <div className='space-y-1' data-testid='process-step-pick-list'>
            <p className='text-xs font-medium text-gray-500'>Pick list</p>
            <div className='flex h-9 items-center'>
              <PrintPickListButton
                scope={{ scope: 'PROCESS_STEP', submissionId, fieldId: processFieldId }}
              />
            </div>
          </div>
        )}

        {/* Assignment — a step cannot start until someone owns it. The picker's
            trigger is a plain <button> with no role of its own, so the testid on
            this wrapper is what makes the control addressable. */}
        {showAssignment && (
          <div className='space-y-1' data-testid='process-step-assignee'>
            <p className='text-xs font-medium text-gray-500'>Assigned</p>
            {/* One control for the whole lifetime of the assignment: the current
                assignee IS the selected value, and picking someone else hands
                the step over. Yourself is just another entry, so claiming and
                delegating are the same gesture. */}
            {busy ? (
              // Replaces the control outright rather than disabling it, so the
              // trigger can say what is happening. Matches the select's box so
              // the row does not shift while the write is in flight.
              <div className='flex h-9 w-[190px] items-center rounded-md border border-input px-3 text-sm text-muted-foreground'>
                Saving…
              </div>
            ) : (
              <SearchableSelect
                value={assigneeId ?? ''}
                options={assigneeOptions}
                placeholder='Assign to…'
                // A COMPLETED step is a closed record. Its owner is part of
                // what was signed off, so it is shown but no longer editable —
                // handing a finished step to someone else would rewrite who
                // was accountable for work that is already attested to.
                disabled={status === 'COMPLETED'}
                triggerTitle={
                  status === 'COMPLETED'
                    ? 'This step is completed — its assignment is part of the closed record'
                    : undefined
                }
                className='w-[190px]'
                // The trigger keeps the row's original width; the panel gets
                // room for a search box and a full name, as AssigneeSelect does.
                panelWidth={240}
                // The roster is every enabled account on the instance, so empty
                // means there is genuinely nobody to assign — not, as the old
                // tenant-filtered list implied, that this tenant has no members
                // yet. Says nothing about the identity provider behind it.
                emptyMessage='No enabled users are available to assign.'
                onChange={id =>
                  runAssignment(() =>
                    id === UNASSIGN_VALUE
                      ? unassign.mutateAsync({ submissionId, fieldId: processFieldId })
                      : assign.mutateAsync({
                          submissionId,
                          fieldId: processFieldId,
                          assigneeId: id,
                        }),
                  )
                }
              />
            )}
          </div>
        )}

        {/* No action column once the step is COMPLETED — the lifecycle is over.
            Ordered last and pushed right: it is the only control here that DOES
            something, and it reads as the end of the row rather than as another
            field. Declared in DOM order beside its siblings so the tab order
            still runs left-to-right through the inputs before reaching it. */}
        {action && (
          <div className='order-last ml-auto space-y-1'>
            <p className='text-xs font-medium text-gray-500'>Action</p>
            <Button
              size='sm'
              disabled={disabled}
              title={startBlockedReason ?? undefined}
              onClick={() => setModalMode(action.mode)}
              className={cn(
                // PRESSABLE, not a value. A flat solid rectangle sitting in a
                // row of captioned columns next to a pastel StatusChip read as
                // one more label — the same shape, the same size, no depth. So
                // it carries the cues a chip never has: a shadow to lift it off
                // the card, and a press that visibly moves.
                // `shadow`, not `shadow-sm`: measured, the small one is a 5%
                // black at 1px, which is invisible against a white card and so
                // fixes nothing about the complaint.
                'h-9 px-5 font-semibold text-white shadow transition-all',
                'hover:shadow-md active:translate-y-px active:shadow-sm',
                // Disabled has to stay a BUTTON too. The base style is
                // `opacity-50` over a solid fill, which is a pale coloured
                // block — indistinguishable from the chips beside it, and the
                // state this control is in most often (an unassigned step).
                // Keeping the silhouette instead — outlined, muted face, dark
                // text — reads as a control that cannot be used yet.
                'disabled:border disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-500 disabled:opacity-100 disabled:shadow-none',
                // Appearance follows the BUTTON's meaning, not the transition
                // behind it. This row's button used to BE the transition, so it
                // matched the modal's confirm colour; at STARTED it now only
                // opens the screen where the stop lives, and wearing that red
                // would promise a halt the click does not perform.
                action.tone === 'green'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-primary hover:bg-primary/90',
              )}
            >
              {/* Play reads as a solid glyph, the check does not — filling
                  CheckCircle2 turns it into a blob. */}
              <action.icon
                className={cn('mr-1.5 h-4 w-4', action.tone !== 'green' && 'fill-current')}
              />
              {action.label}
            </Button>
          </div>
        )}

        <div className='space-y-1'>
          <p className='text-xs font-medium text-gray-500'>Status</p>
          <div className='flex h-9 items-center'>
            <StatusChip status={status} assigned={assigneeId != null} />
          </div>
        </div>

        {/* The step's expected start, offered here so it can be set before the
            dialog is ever opened — it is what drives the FEFO proposal and the
            equipment holds, so asking for it early is asking for it at the
            useful moment. The SAME value the dialog edits (it lives on the
            card's start draft), never a second copy.

            Read-only once the run exists: after Start the plan is part of the
            signed record, and the row is not where a signed record is edited. */}
        {!preview && configured && (status === 'ASSIGNED' || execution?.plannedStart) && (
          <div className='space-y-1'>
            {/* ON THE LABEL LINE, not under the input. Every other column here
                is a label over a single h-9 control, and hanging a chip below
                this one made its column taller than the rest — the whole row
                stopped lining up. Beside the label it costs no height. */}
            <p className='flex h-4 items-center gap-1.5 text-xs font-medium text-gray-500'>
              Expected start
              {/* Says what the field is FOR. Without it the control reads as one
                  more optional date on a form, and the operator never learns
                  that applying it is what makes the step assemble itself.
                  Keyed on the automation not having run, not on the field being
                  empty: a step seeded from the run's scheduled date shows a
                  date and still has its equipment and material to pick. And
                  only where there IS something to assemble — on a step
                  configuring neither, the badge would promise nothing. */}
              {status === 'ASSIGNED' && !automationSet && hasAutomatableWork && (
                <span className='inline-flex shrink-0 items-center gap-0.5 rounded-full bg-blue-50 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-blue-700'>
                  <Zap className='h-2.5 w-2.5' />
                  auto
                </span>
              )}
              {status === 'ASSIGNED' && (
                <AutomationStatus
                  held={cardHeldCount}
                  total={cardEquipmentCount}
                  busy={cardRetime.isPending}
                  busyLabel='Moving holds…'
                  // Matches the AUTO badge's padding so the two read as siblings
                  // and neither grows the label line.
                  className='py-px'
                />
              )}
            </p>
            {status === 'ASSIGNED' ? (
              <>
                {/* h-9 to match the Status and Assigned columns beside it. */}
                <div className='flex h-9 items-center gap-1.5'>
                  {/* EXPLICIT APPLY, AND IT LIVES IN THE PICKER.
                      This was a native `datetime-local` with a tick and a cross
                      beside it. The browser's own popup has no notion of
                      "done" and no place to put one — it is drawn outside the
                      document — so the operator picked a day and a time in one
                      panel and then had to find the confirm somewhere else,
                      often behind the popup still covering it. The calendar is
                      ours now (`DateTimePanel`, shared with the procedures
                      list) and Apply sits in it.

                      What has not changed: nothing is applied on blur or on
                      selection. Setting this is what RUNS the automation, which
                      books machines and holds material, and "I clicked away" is
                      not consent for that. */}
                  {/* Closing does NOT revert, which is this field's existing
                      contract: clicking off leaves the draft sitting there and
                      Escape is what puts it back. Reverting on every close
                      would also fire on the programmatic close after Apply —
                      resetting `editingAutomation` and the draft just as the
                      re-time dialog reads them. */}
                  <Popover open={expectedStartOpen} onOpenChange={setExpectedStartOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type='button'
                        aria-label='Expected start for this process step'
                        // ONCE THE AUTOMATION HAS RUN THIS IS NOT A FREE FIELD.
                        // Machines are booked against it, so setting a new time
                        // here would leave the field and the holds describing
                        // different plans. The pencil beside it is the way in,
                        // and it re-times the bookings rather than just
                        // unlocking the control. Locked while unassigned,
                        // because setting this is what runs the automation.
                        disabled={
                          (automationSet && !editingAutomation) || !!automationBlockedReason
                        }
                        title={automationBlockedReason ?? 'Click to change'}
                        className={cn(
                          'flex h-9 w-[15rem] items-center gap-2 rounded-md border px-3 text-left text-sm tabular-nums transition-colors',
                          (automationSet && !editingAutomation) || automationBlockedReason
                            ? 'border-gray-200 bg-gray-100 text-gray-600'
                            : 'border-gray-200 bg-white hover:border-gray-300',
                          // Unset is the state that costs the operator
                          // something, so it is the one that draws the eye —
                          // not an error, just an invitation. Not while it is
                          // blocked, though: inviting a click on a control that
                          // cannot be used is the opposite of helpful.
                          !expectedStartValue &&
                            !automationBlockedReason &&
                            'border-blue-300 hover:border-blue-400',
                        )}
                      >
                        <CalendarClock className='h-3.5 w-3.5 shrink-0 text-gray-400' />
                        {/* Never an empty mask: unset reads as the invitation. */}
                        <span className={cn(!expectedStartValue && 'text-muted-foreground')}>
                          {expectedStartValue
                            ? formatPlanned(fromLocalInput(expectedStartValue))
                            : 'Set an expected start'}
                        </span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align='start'
                      className='w-auto p-0'
                      // Escape puts the stored plan back, as it did when this
                      // was an input.
                      onEscapeKeyDown={() => cancelExpectedStartEdit()}
                    >
                      <DateTimePanel
                        value={expectedStartValue}
                        onChange={setExpectedStartDraft}
                        disabled={setPlannedStart.isPending || cardRetime.isPending}
                        blocker={
                          automationBlockedReason ??
                          (canApplyExpectedStart ? null : 'Pick a day and a time')
                        }
                      />
                      <div className='flex items-center justify-end gap-2 border-t px-3 py-3'>
                        <Button
                          type='button'
                          size='sm'
                          variant='ghost'
                          disabled={setPlannedStart.isPending || cardRetime.isPending}
                          // Explicit Cancel is the one route that DOES revert.
                          onClick={() => {
                            cancelExpectedStartEdit()
                            setExpectedStartOpen(false)
                          }}
                          aria-label='Cancel editing the expected start'
                          className='h-7 px-2 text-xs'
                        >
                          Cancel
                        </Button>
                        <Button
                          type='button'
                          size='sm'
                          disabled={
                            !canApplyExpectedStart ||
                            setPlannedStart.isPending ||
                            cardRetime.isPending
                          }
                          onClick={() => void applyExpectedStart()}
                          title={
                            automationBlockedReason ??
                            (editingAutomation
                              ? 'Apply this time — the reserved units move with it'
                              : !canApplyExpectedStart
                                ? 'Pick a date first'
                                : hasAutomatableWork
                                  ? 'Apply this date and select equipment and material'
                                  : 'Apply this date')
                          }
                          aria-label='Apply this expected start'
                          className='h-7 gap-1.5 px-3 text-xs'
                        >
                          {setPlannedStart.isPending ? (
                            <Loader2 className='h-3.5 w-3.5 animate-spin' />
                          ) : (
                            <Check className='h-3.5 w-3.5' />
                          )}
                          {/* The automation is already booked, so this MOVES
                              holds rather than taking them. Different act,
                              different word. */}
                          {editingAutomation ? 'Re-time' : 'Apply'}
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>

                  {automationSet && !editingAutomation && (
                    <button
                      type='button'
                      // Unlocks the control AND opens it: the pencil's whole
                      // purpose is to get back to the picker, and leaving it
                      // merely unlocked cost a second click on a control that
                      // had just visibly changed state.
                      onClick={() => {
                        setEditingAutomation(true)
                        setExpectedStartOpen(true)
                      }}
                      aria-label='Edit the automation’s expected start'
                      title='Change the time — the reserved units move with it'
                      className='flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-200 text-gray-500 transition-colors hover:border-blue-300 hover:text-blue-600'
                    >
                      <Pencil className='h-3.5 w-3.5' />
                    </button>
                  )}
                </div>

                {/* The one thing still allowed to grow this column. A refused
                    re-time is exceptional and momentary, and a message the
                    operator cannot see is worse than a row that shifts. */}
                {/* Re-time refusals live in the confirm dialog, next to the
                    change they refused. A refused SAVE has no dialog to live
                    in — the operator applied a date and nothing happened — so
                    it is stated here. */}
                {expectedStartError && (
                  <p className='text-xs font-medium text-red-600'>{expectedStartError}</p>
                )}

                {/* No "assign this step first" line here: `startBlockedReason`
                    already says it a few pixels away, and two amber sentences
                    making the same point read as two different problems. The
                    field's own tooltip carries the reason for anyone who lands
                    on it directly. */}
              </>
            ) : (
              <div className='flex h-9 items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50/70 px-3 text-sm tabular-nums text-gray-700'>
                <CalendarClock className='h-3.5 w-3.5 shrink-0 text-gray-400' />
                {formatStamp(execution!.plannedStart!)}
              </div>
            )}
          </div>
        )}
      </div>

      {!preview && !configured && (
        <p className='mt-2 text-xs text-muted-foreground'>
          No process configured for this field yet — define it on the form builder&apos;s Process
          tab.
        </p>
      )}
      {/* A disabled button with no stated reason is what operators file bugs about.
          And a stated reason with no way to act on it is the next bug: both of
          these refusals are fixed by the same one thing, so the fix is offered
          here rather than left to be found in the assignee dropdown above.

          The takeover is EXPLICIT on purpose. Start could have claimed the step
          silently, but that quietly moves regulated work off a named colleague;
          in a Part 11 workflow taking a step over is an act someone performs,
          not a side effect of pressing another button. The verb changes with
          the case — claiming something unowned is not the same as taking it off
          a person, and the label should not pretend otherwise. */}
      {!preview && configured && startBlockedReason && (
        <div className='mt-2 flex flex-wrap items-center gap-2'>
          <p className='text-xs text-amber-700'>{startBlockedReason}</p>
          {canExecute && !locked && user?.userId && (
            <Button
              type='button'
              size='sm'
              variant='outline'
              disabled={assign.isPending}
              onClick={() =>
                runAssignment(() =>
                  assign.mutateAsync({
                    submissionId,
                    fieldId: processFieldId,
                    // Self-claim: the server takes an absent assignee as "me",
                    // and only that path is open without `process.assign`.
                    // Naming the id explicitly would make this a delegation to
                    // whoever happens to be signed in, which is a different
                    // route with a different permission.
                    assigneeId: undefined,
                  }),
                )
              }
              className='h-6 gap-1.5 px-2 text-xs'
            >
              {assign.isPending && <Loader2 className='h-3 w-3 animate-spin' />}
              {assigneeId == null ? 'Assign to me' : 'Take this over'}
            </Button>
          )}
        </div>
      )}
      {assignError && <p className='mt-2 text-xs font-medium text-red-600'>{assignError}</p>}
      {execution?.actualStart && (
        <p className='mt-2 text-xs text-muted-foreground'>
          Started {new Date(execution.actualStart).toLocaleString()}
          {execution.performedBy ? ` by ${execution.performedBy}` : ''}
          {execution.actualEnd ? ` · ended ${new Date(execution.actualEnd).toLocaleString()}` : ''}
        </p>
      )}

      {/* Not gated on `autoSelectStartIso` the way the dialog below is: this one
          asks about MOVING existing holds, which is exactly the case where the
          draft start may be absent — a page reload keeps the bookings and loses
          the draft. */}
      {connection && (
        <RetimeConfirmDialog
          open={cardRetimeAsking}
          stepName={field.label}
          currentStart={
            startDraft?.expectedStart
              ? formatStamp(localToIso(startDraft.expectedStart) ?? '')
              : null
          }
          newStart={cardRetimeDraftIso ? formatStamp(cardRetimeDraftIso) : ''}
          preview={cardRetimeOutcome}
          busy={cardRetime.isPending}
          error={cardRetimeError}
          formatStamp={formatStamp}
          onConfirm={commitCardRetime}
          onCancel={() => {
            setCardRetimeAsking(false)
            setCardRetimeError(null)
          }}
        />
      )}

      {connection && autoSelectStartIso && (
        <AutoSelectConfirmDialog
          open={autoSelectOpen}
          stepName={field.label}
          expectedStart={formatStamp(autoSelectStartIso)}
          equipmentWindowStart={
            // The EARLIEST window, not the first requirement's: prep is per
            // unit now, so declaration order no longer implies which is wanted
            // first. Same-format UTC strings, so lexical order is chronological.
            autoSelectWindows.length > 0
              ? formatStamp(
                  autoSelectWindows
                    .map(w => w.from)
                    .reduce((min, f) => (f < min ? f : min)),
                )
              : null
          }
          equipment={autoSelectEquipment}
          proposals={autoSelectProposals ?? null}
          // The CONFIGURED materials, so the section is driven by the process
          // definition the same way `equipment` is — a requirement the proposal
          // could not resolve still has to appear, or the preview quietly
          // under-reports what the run needs.
          inventory={connection?.inventoryRequirements ?? []}
          proposalsFailed={autoSelectProposalsFailed}
          // Straight off the connection — staffing needs no availability query
          // because nothing is being allocated. It is what the step costs in
          // people, reported next to what is actually being picked.
          staffing={connection?.staffingLines ?? []}
          loading={autoSelectLoading || autoSelectAvailLoading}
          busy={reserveForAutoSelect.isPending}
          error={autoSelectError}
          onConfirm={runAutoSelect}
          onCancel={() => {
            setAutoSelectOpen(false)
            setAutoSelectError(null)
          }}
        />
      )}

      {connection && (
        <ProcessStepActionModal
          field={field}
          processFieldId={processFieldId}
          mode={modalMode}
          connection={connection}
          execution={execution}
          submissionId={submissionId}
          scheduledStart={executionsData?.scheduledStart ?? null}
          kit={
            connection.isKitStep
              ? { supplyId: execution?.kitSupplyId ?? null, expiry: kitExpiry }
              : null
          }
          onClose={() => setModalMode(null)}
          onStarted={() => {
            setStartDraft(null)
            setModalMode('end')
          }}
          onTransitioned={() => onTransitioned?.()}
          startDraft={startDraft}
          onStartDraftChange={setStartDraft}
        />
      )}
    </div>
  )
}
